import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from "react";
import {
  UI_PREFS_APPLIED_EVENT,
  UiPrefs,
  recordUiChoice,
} from "../storage/uiPreferences";

interface Translations {
  [key: string]: {
    en: string;
    ar: string;
  };
}

const translations: Translations = {
  // ── App update / force-reload prompt ──
  update_available_title: {
    en: "A new version is available",
    ar: "يتوفر إصدار جديد",
  },
  update_available_message: {
    en: "The app has been updated. Please reload to get the latest version.",
    ar: "تم تحديث التطبيق. يرجى إعادة التحميل للحصول على أحدث إصدار.",
  },
  reload_now: {
    en: "Reload Now",
    ar: "إعادة التحميل الآن",
  },
  // ── IMA ID QR verification (DMIS-unavailable fallback) ──
  qr_enter_value: {
    en: "Please scan or enter your ID card QR.",
    ar: "يرجى مسح أو إدخال رمز QR الخاص ببطاقتك.",
  },
  qr_verify_failed: {
    en: "Verification failed. Please try again.",
    ar: "فشل التحقق. يرجى المحاولة مرة أخرى.",
  },
  qr_scan_title: {
    en: "Verify your ID card",
    ar: "تحقّق من بطاقة هويتك",
  },
  qr_scan_subtitle: {
    en: "We could not find your record. Please verify using your syndicate ID card.",
    ar: "لم نتمكن من العثور على سجلك. يرجى التحقق باستخدام بطاقة هويتك النقابية.",
  },
  qr_scan_subtitle_expired: {
    en: "Your membership needs renewal. Scan your ID card to continue.",
    ar: "عضويتك بحاجة إلى تجديد. امسح بطاقة هويتك للمتابعة.",
  },
  qr_scan_instruction: {
    en: "Scan the QR on your card with your phone camera and paste the link, or type the number printed under the QR.",
    ar: "امسح رمز QR على بطاقتك بكاميرا هاتفك وألصق الرابط، أو اكتب الرقم المطبوع أسفل الرمز.",
  },
  qr_input_placeholder: {
    en: "https://v.ima.org.iq/... or the QR number",
    ar: "https://v.ima.org.iq/... أو رقم الـ QR",
  },
  qr_open_camera: {
    en: "Open camera to scan QR",
    ar: "افتح الكاميرا لمسح رمز QR",
  },
  qr_close_camera: {
    en: "Close camera",
    ar: "إغلاق الكاميرا",
  },
  qr_point_camera: {
    en: "Point your camera at the QR code on your ID card.",
    ar: "وجّه الكاميرا نحو رمز QR الموجود على بطاقة هويتك.",
  },
  qr_camera_error: {
    en: "Could not access the camera. Please allow camera access or enter the code manually.",
    ar: "تعذّر الوصول إلى الكاميرا. يرجى السماح بالوصول إلى الكاميرا أو إدخال الرمز يدويًا.",
  },
  qr_or_paste: {
    en: "or paste the link / enter the number",
    ar: "أو ألصق الرابط / أدخل الرقم",
  },
  qr_enter_phone_title: {
    en: "Enter your phone number",
    ar: "أدخل رقم هاتفك",
  },
  qr_enter_phone_subtitle: {
    en: "We will send a one-time code to confirm your number.",
    ar: "سنرسل رمزًا لمرة واحدة لتأكيد رقمك.",
  },
  qr_invalid_phone: {
    en: "Enter a valid Iraqi mobile: 10 digits starting with 75, 76, 77, 78 or 79.",
    ar: "أدخل رقم هاتف عراقي صحيح: 10 أرقام يبدأ بـ 75 أو 76 أو 77 أو 78 أو 79.",
  },
  iraqi_phone_hint: {
    en: "Iraqi mobile only — 10 digits starting with 75/76/77/78/79 (e.g. 7701234567).",
    ar: "رقم عراقي فقط — 10 أرقام يبدأ بـ 75/76/77/78/79 (مثال: 7701234567).",
  },
  qr_session_expired: {
    en: "Your verification session expired. Please scan your card again.",
    ar: "انتهت صلاحية جلسة التحقق. يرجى مسح بطاقتك مرة أخرى.",
  },
  qr_manual_reg_required: {
    en: "Registration number is required.",
    ar: "رقم التسجيل مطلوب.",
  },
  qr_manual_expired: {
    en: "This membership expiry date is in the past. Your membership must be valid (not expired).",
    ar: "تاريخ انتهاء العضوية في الماضي. يجب أن تكون عضويتك سارية (غير منتهية).",
  },
  license_expiry_in_past: {
    en: "The registration expiry date is in the past. Please enter a valid (future) date.",
    ar: "تاريخ انتهاء التسجيل في الماضي. يرجى إدخال تاريخ صالح (في المستقبل).",
  },
  // QR-fallback required documents
  residence_card_front: { en: "Residence Card (Front)", ar: "بطاقة السكن (الوجه الأمامي)" },
  residence_card_back: { en: "Residence Card (Back)", ar: "بطاقة السكن (الوجه الخلفي)" },
  doctor_photo: { en: "Doctor Photo", ar: "صورة للطبيب" },
  civil_id_front: { en: "Civil / Unified ID (Front)", ar: "هوية الأحوال المدنية أو البطاقة الموحدة (الوجه الأمامي)" },
  civil_id_back: { en: "Civil / Unified ID (Back)", ar: "هوية الأحوال المدنية أو البطاقة الموحدة (الوجه الخلفي)" },
  please_upload_residence_card_front: { en: "Please upload the front of the residence card.", ar: "يرجى رفع الوجه الأمامي لبطاقة السكن." },
  please_upload_residence_card_back: { en: "Please upload the back of the residence card.", ar: "يرجى رفع الوجه الخلفي لبطاقة السكن." },
  please_upload_doctor_photo: { en: "Please upload the doctor's photo.", ar: "يرجى رفع صورة الطبيب." },
  please_upload_civil_id_front: { en: "Please upload the front of the civil/unified ID.", ar: "يرجى رفع الوجه الأمامي لهوية الأحوال المدنية أو البطاقة الموحدة." },
  please_upload_civil_id_back: { en: "Please upload the back of the civil/unified ID.", ar: "يرجى رفع الوجه الخلفي لهوية الأحوال المدنية أو البطاقة الموحدة." },
  graduation_certificate: { en: "Graduation Certification", ar: "تصديق التخرج" },
  please_upload_graduation_certificate: { en: "Please upload the graduation certification.", ar: "يرجى رفع تصديق التخرج." },
  skip_payment_staging: { en: "Skip payment (staging only)", ar: "تخطّي الدفع (بيئة الاختبار فقط)" },
  qr_manual_pending_review: {
    en: "Your data was submitted and is pending staff review.",
    ar: "تم إرسال بياناتك وهي قيد مراجعة الموظفين.",
  },
  qr_manual_submitted_title: {
    en: "Submission received",
    ar: "تم استلام الطلب",
  },
  qr_manual_title: {
    en: "Enter your ID details",
    ar: "أدخل تفاصيل بطاقتك",
  },
  qr_manual_subtitle: {
    en: "Automatic verification is unavailable. Enter your details from the official IMA page.",
    ar: "التحقق التلقائي غير متاح. أدخل بياناتك من صفحة النقابة الرسمية.",
  },
  qr_manual_open_page: {
    en: "Open the official verification page",
    ar: "افتح صفحة التحقق الرسمية",
  },
  qr_manual_confirm_loaded: {
    en: "I opened the page and can see my data.",
    ar: "لقد فتحت الصفحة وأستطيع رؤية بياناتي.",
  },
  qr_manual_reg_num: {
    en: "Registration number",
    ar: "رقم التسجيل",
  },
  qr_manual_name_ar: {
    en: "Full name (Arabic)",
    ar: "الاسم الكامل (بالعربية)",
  },
  qr_manual_name_en: {
    en: "Full name (English)",
    ar: "الاسم الكامل (بالإنجليزية)",
  },
  qr_manual_profession: {
    en: "Profession",
    ar: "المهنة",
  },
  qr_manual_expiry: {
    en: "Membership expiry date",
    ar: "تاريخ انتهاء العضوية",
  },
  qr_manual_review_note: {
    en: "Manually entered data is not trusted automatically — your account will remain pending staff review until approved.",
    ar: "البيانات المُدخلة يدويًا لا تُعتمد تلقائيًا — سيبقى حسابك قيد مراجعة الموظفين حتى تتم الموافقة.",
  },
  welcome: {
    en: "Welcome to RX360",
    ar: "مرحباً بكم في RX360",
  },
  subtitle: {
    en: "Enter your phone number to continue",
    ar: "أدخل رقم هاتفك للمتابعة",
  },
  platform_title: {
    en: "Electronic Doctor Registration Platform",
    ar: "منصة تسجيل الأطباء الالكترونية",
  },
  platform_description: {
    en: "Under the supervision of the Iraqi Medical Association, we put at your fingertips an advanced tool that makes your work faster and more professional",
    ar: "تحت إشراف نقابة الأطباء العراقية، تضع بين يديك أداة متطورة تجعل عملك أسرع وأكثر احترافية",
  },
  enter_phone: {
    en: "Please enter your phone number to continue",
    ar: "يرجى ادخال رقم الهاتف للمتابعة",
  },
  phone_number: {
    en: "Phone Number",
    ar: "رقم الهاتف",
  },
  phone_must_be_10_digits: {
    en: "Phone number must be exactly 10 digits",
    ar: "يجب أن يتكون رقم الهاتف من 10 أرقام بالضبط",
  },
  country_code: {
    en: "Country",
    ar: "البلد",
  },
  continue: {
    en: "Continue",
    ar: "متابعة",
  },
  back: {
    en: "Back",
    ar: "رجوع",
  },
  privacy_policy: {
    en: "Privacy Policy",
    ar: "سياسة الخصوصية",
  },
  privacy_text: {
    en: "We guarantee that your data is fully protected and will not be used for any unauthorized purposes. Read more.",
    ar: "نضمن أن بياناتك محمية تماما ولن يتم استخدامها لأي أغراض غير مصرح بها. اقرأ المزيد.",
  },
  terms_text: {
    en: "By continuing, you agree to our Terms of Service and Privacy Policy",
    ar: "بالمتابعة، أنت توافق على شروط الخدمة وسياسة الخصوصية",
  },
  // OTP Screen translations
  verify_phone: {
    en: "Verify Your Phone",
    ar: "تأكيد رقم الهاتف",
  },
  enter_code_sent_to: {
    en: "Enter the 6-digit code sent to",
    ar: "ادخل رقم التأكيد المرسل إلى",
  },
  verify_otp: {
    en: "Verify OTP",
    ar: "تأكيد",
  },
  resend_otp: {
    en: "Resend OTP",
    ar: "إرسال مرة أخرى",
  },
  resend_otp_in: {
    en: "Resend OTP in",
    ar: "إعادة الإرسال خلال",
  },
  seconds: {
    en: "s",
    ar: "ث",
  },
  otp_locked_title: {
    en: "Too many attempts",
    ar: "محاولات كثيرة جدًا",
  },
  otp_locked_retry_in: {
    en: "Your account is temporarily locked. You can try again in:",
    ar: "تم إيقاف الحساب مؤقتًا. يمكنك المحاولة مرة أخرى خلال:",
  },
  otp_send_limited_title: {
    en: "Too many code requests",
    ar: "طلبات رمز كثيرة",
  },
  otp_send_limited_retry_in: {
    en: "To protect your account, wait before asking for a new code. You can request one again in:",
    ar: "لحماية حسابك، انتظر قبل طلب رمز جديد. يمكنك طلب رمز جديد بعد:",
  },
  otp_wait_title: {
    en: "Too many attempts",
    ar: "محاولات كثيرة",
  },
  otp_wait_retry_in: {
    en: "Wait a little, then try again. You can try again in:",
    ar: "انتظر قليلاً ثم أعد المحاولة. يمكنك المحاولة مرة أخرى بعد:",
  },
  qr_identity_exists_title: {
    en: "This licence already has an account",
    ar: "لديك حساب مسجّل بهذا الرقم",
  },
  qr_identity_exists_body: {
    en: "An account already exists for this licence number. Sign in with your licence number and the phone number registered on it. If that phone number has changed, contact the syndicate.",
    ar: "يوجد حساب مسجّل برقم الإجازة هذا. سجّل الدخول برقم الإجازة ورقم الهاتف المسجّل عليه. إذا تغيّر رقم هاتفك، راجع النقابة.",
  },
  sign_in_with_licence: {
    en: "Sign in with the licence number",
    ar: "تسجيل الدخول برقم الإجازة",
  },
  didnt_receive_code: {
    en: "Didn't receive the code? Check your SMS messages or wait to resend.",
    ar: "لم يصلك الكود؟ إرسال مرة أخرى",
  },
  enter_valid_otp: {
    en: "Please enter a valid 6-digit OTP",
    ar: "يرجى إدخال رقم التأكيد المكون من 6 أرقام",
  },
  invalid_otp: {
    en: "Invalid OTP",
    ar: "رقم التأكيد غير صحيح",
  },
  change_phone_number: {
    en: "change",
    ar: "تغيير",
  },
  // License-based OTP flow translations
  enter_license_number: {
    en: "Please enter your license number",
    ar: "يرجى إدخال رقم التسجيل",
  },
  invalid_license_number: {
    en: "Invalid license number",
    ar: "رقم التسجيل غير صحيح",
  },
  confirm_phone_number: {
    en: "Confirm Phone Number",
    ar: "تأكيد رقم الهاتف",
  },
  we_will_send_otp: {
    en: "We will send you a verification code",
    ar: "سنرسل لك رمز التحقق",
  },
  phone_ending_with: {
    en: "Phone number ending with",
    ar: "رقم الهاتف المنتهي بـ",
  },
  otp_will_be_sent_to_phone: {
    en: "A verification code will be sent to this number",
    ar: "سيتم إرسال رمز التحقق إلى هذا الرقم",
  },
  confirm_and_send_otp: {
    en: "Confirm and Send Code",
    ar: "تأكيد وإرسال الرمز",
  },
  change_license_number: {
    en: "change",
    ar: "تغيير",
  },
  open_camera: {
    en: "Open Camera",
    ar: "افتح الكاميرا",
  },
  enter_manually: {
    en: "Enter Manually",
    ar: "أدخل يدوياً",
  },
  enter_id_number: {
    en: "Enter ID Number",
    ar: "أدخل رقم الهوية",
  },
  enter_id_number_instruction: {
    en: "Enter the ID number from your Iraqi Medical Association card",
    ar: "أدخل رقم الهوية من بطاقة نقابة الأطباء العراقية",
  },
  id_number_placeholder: {
    en: "Enter ID number",
    ar: "أدخل رقم الهوية",
  },
  submit: {
    en: "Submit",
    ar: "إرسال",
  },
  logout: {
    en: "Logout",
    ar: "تسجيل الخروج",
  },
  face_live_title: {
    en: "Face Live Verification",
    ar: "التحقق من الوجه المباشر",
  },
  face_live_subtitle: {
    en: "We'll automatically capture 5 photos of your face",
    ar: "سنقوم بالتقاط 5 صور لوجهك تلقائياً",
  },
  position_face: {
    en: "Position your face in the center",
    ar: "ضع وجهك في المنتصف",
  },
  ensure_lighting: {
    en: "Ensure good lighting",
    ar: "تأكد من الإضاءة الجيدة",
  },
  remove_accessories: {
    en: "Remove hats, glasses, masks",
    ar: "انزع القبعات والنظارات والأقنعة",
  },
  auto_capture_info: {
    en: "Photos taken automatically (3s first, 2s between)",
    ar: "يتم التقاط الصور تلقائياً (3 ثوان أولاً، ثانيتان بين الصور)",
  },
  start_face_capture: {
    en: "Start Face Capture",
    ar: "بدء التقاط الوجه",
  },
  face_live_capture: {
    en: "Face Live Capture",
    ar: "التقاط الوجه المباشر",
  },
  auto_capturing: {
    en: "Auto Capturing...",
    ar: "جاري التقاط الصور...",
  },
  positioning_face: {
    en: "Positioning your face...",
    ar: "ضع وجهك في الموضع الصحيح...",
  },
  all_photos_captured: {
    en: "All photos captured!",
    ar: "تم التقاط جميع الصور!",
  },
  look_straight: {
    en: "Look straight at the camera",
    ar: "انظر مباشرة إلى الكاميرا",
  },
  turn_left: {
    en: "Turn your head slightly to the left",
    ar: "أدر رأسك قليلاً إلى اليسار",
  },
  turn_right: {
    en: "Turn your head slightly to the right",
    ar: "أدر رأسك قليلاً إلى اليمين",
  },
  look_up: {
    en: "Look up slightly",
    ar: "انظر إلى الأعلى قليلاً",
  },
  look_straight_again: {
    en: "Look straight at the camera again",
    ar: "انظر مباشرة إلى الكاميرا مرة أخرى",
  },
  // Face Verification translations
  face_verification: {
    en: "Face Verification",
    ar: "التحقق من الهوية",
  },
  start_face_verification: {
    en: "Start Face Verification",
    ar: "بدء التحقق من الهوية",
  },
  face_verification_title: {
    en: "Face Verification & Live Capture",
    ar: "التحقق من الهوية والتقاط الوجه المباشر",
  },
  face_verification_subtitle: {
    en: "We will verify your identity and take 5 photos of your face",
    ar: "سنتحقق من هويتك ونلتقط 5 صور لوجهك",
  },
  security_verification_process: {
    en: "Security & Verification Process",
    ar: "عملية الأمان والتحقق",
  },
  real_face_detection: {
    en: "Real face detection (anti-spoofing)",
    ar: "كشف الوجه الحقيقي (مضاد للتزوير)",
  },
  identity_verification_id: {
    en: "Identity verification with your ID photo",
    ar: "التحقق من الهوية مع صورة الهوية الخاصة بك",
  },
  follow_head_movements: {
    en: "Follow head movement prompts",
    ar: "اتبع إرشادات حركة الرأس",
  },
  verified_photos_automatic: {
    en: "5 verified photos captured automatically",
    ar: "5 صور محققة يتم التقاطها تلقائياً",
  },
  instructions: {
    en: "Instructions",
    ar: "التعليمات",
  },
  same_person_id_photo: {
    en: "You must be the same person as in your ID photo",
    ar: "يجب أن تكون نفس الشخص الموجود في صورة الهوية",
  },
  previous_verification_failed: {
    en: "Previous Verification Failed",
    ar: "فشل التحقق السابق",
  },
  loading_face_models: {
    en: "Loading face verification models...",
    ar: "جاري تحميل نماذج التحقق من الوجه...",
  },
  // Error messages
  camera_not_ready: {
    en: "Camera not ready. Please wait...",
    ar: "الكاميرا غير جاهزة. يرجى الانتظار...",
  },
  no_face_detected_position: {
    en: "No face detected. Please position your face clearly in the camera.",
    ar: "لم يتم اكتشاف وجه. يرجى وضع وجهك بوضوح في الكاميرا.",
  },
  ensure_proper_lighting: {
    en: "Please ensure proper lighting and look directly at the camera.",
    ar: "يرجى التأكد من الإضاءة المناسبة والنظر مباشرة إلى الكاميرا.",
  },
  face_not_match_id: {
    en: "Face does not match your ID photo. Please ensure you are the same person.",
    ar: "الوجه لا يطابق صورة الهوية. يرجى التأكد من أنك نفس الشخص.",
  },
  id_photo_not_available: {
    en: "ID photo not available for comparison. Please restart the process.",
    ar: "صورة الهوية غير متوفرة للمقارنة. يرجى إعادة تشغيل العملية.",
  },
  failed_capture_image: {
    en: "Failed to capture image. Please try again.",
    ar: "فشل في التقاط الصورة. يرجى المحاولة مرة أخرى.",
  },
  verification_technical_error: {
    en: "Verification failed due to technical error. Please try again.",
    ar: "فشل التحقق بسبب خطأ تقني. يرجى المحاولة مرة أخرى.",
  },
  // Status messages
  face_ready_capture: {
    en: "Face Ready for Capture",
    ar: "الوجه جاهز للتصوير",
  },
  verifying_face: {
    en: "Verifying Face...",
    ar: "جاري التحقق من الوجه...",
  },
  position_your_face: {
    en: "Position Your Face",
    ar: "ضع وجهك في الموضع الصحيح",
  },
  retrying: {
    en: "(Retrying...)",
    ar: "(جاري إعادة المحاولة...)",
  },
  // Retry and manual capture messages
  having_trouble_automatic: {
    en: "Having trouble detecting your face automatically. You can try manual capture.",
    ar: "مشكلة في اكتشاف وجهك تلقائياً. يمكنك تجربة التصوير اليدوي.",
  },
  no_face_detected_circle: {
    en: "No face detected. Please position your face in the camera circle.",
    ar: "لم يتم اكتشاف وجه. يرجى وضع وجهك في دائرة الكاميرا.",
  },
  having_trouble_detection: {
    en: "Having trouble with automatic detection?",
    ar: "تواجه مشكلة مع الكشف التلقائي؟",
  },
  capture_photo_manually: {
    en: "📸 Capture Photo Manually",
    ar: "📸 التقط الصورة يدوياً",
  },
  capture_current_frame: {
    en: "This will capture the current frame",
    ar: "سيؤدي هذا إلى التقاط الإطار الحالي",
  },
  // Attempt counter
  attempt: {
    en: "Attempt",
    ar: "المحاولة",
  },
  confidence: {
    en: "Confidence",
    ar: "الثقة",
  },
  face_verification_failed_multiple: {
    en: "Face verification failed multiple times. Please ensure you are the same person as in your ID photo and try again.",
    ar: "فشل التحقق من الوجه عدة مرات. يرجى التأكد من أنك نفس الشخص الموجود في صورة الهوية والمحاولة مرة أخرى.",
  },
  confirm_doctor_info: {
    en: "Confirm Doctor Information",
    ar: "تأكيد بيانات الطبيب",
  },
  review_info_message: {
    en: "Please review your information and confirm to continue",
    ar: "يرجى مراجعة البيانات والتأكيد للمتابعة",
  },
  registration_number: {
    en: "Registration Number",
    ar: "رقم التسجيل",
  },
  // The clinic's opening licence (clinic.license_number). Not `license_number`, which is the
  // doctor's syndicate registration number on the sign-in screens and reads «رقم التسجيل».
  clinic_opening_license_number: {
    en: "Opening licence no.",
    ar: "رقم إجازة فتح العيادة",
  },
  id_number: {
    en: "ID Number",
    ar: "رقم الهوية",
  },
  registration_date: {
    en: "Registration Date",
    ar: "تاريخ التسجيل",
  },
  print_date: {
    en: "Print Date",
    ar: "تاريخ الطباعة",
  },
  renewal_date: {
    en: "Renewal Date",
    ar: "تاريخ التجديد",
  },
  expiry_date: {
    en: "Expiry Date",
    ar: "تاريخ النفاذ",
  },
  status: {
    en: "Status",
    ar: "الحالة",
  },
  confirm_and_continue: {
    en: "Confirm and Continue",
    ar: "تأكيد ومتابعة",
  },
  info_readonly_notice: {
    en: "This information cannot be edited and was obtained from the Iraqi Medical Association",
    ar: "هذه المعلومات غير قابلة للتعديل وتم الحصول عليها من نقابة الأطباء العراقية",
  },
  // Face live additional translations
  uploading_face_photos: {
    en: "Uploading Face Photos",
    ar: "جاري رفع صور الوجه",
  },
  complete: {
    en: "complete",
    ar: "مكتمل",
  },
  processing_photos: {
    en: "Please wait while we process your photos...",
    ar: "يرجى الانتظار بينما نقوم بمعالجة الصور...",
  },
  upload_complete: {
    en: "Upload Complete!",
    ar: "اكتمل الرفع!",
  },
  photos_uploaded_success: {
    en: "Your face photos have been successfully uploaded.",
    ar: "تم رفع صور وجهك بنجاح.",
  },
  proceeding_next: {
    en: "Proceeding to next step...",
    ar: "الانتقال إلى الخطوة التالية...",
  },
  provided_permission: {
    en: "I have provided permission",
    ar: "لقد قدمت الإذن",
  },
  unable_access_camera: {
    en: "Unable to access your camera",
    ar: "غير قادر على الوصول إلى الكاميرا",
  },
  camera_access_needed: {
    en: "To continue the verification, we need access to your device's camera.",
    ar: "لمتابعة التحقق، نحتاج إلى الوصول إلى كاميرا جهازك.",
  },
  enable_camera_icon: {
    en: "Make sure the camera icon on your address bar is enabled",
    ar: "تأكد من تمكين أيقونة الكاميرا في شريط العناوين",
  },
  camera_always_allow: {
    en: 'Set your camera settings to "always allow"',
    ar: 'اضبط إعدادات الكاميرا على "السماح دائماً"',
  },
  camera_help_info: {
    en: "For more information, visit",
    ar: "لمزيد من المعلومات، قم بزيارة",
  },
  camera_help: {
    en: "camera help",
    ar: "مساعدة الكاميرا",
  },
  allow_camera_access: {
    en: "Allow Camera Access",
    ar: "السماح بالوصول للكاميرا",
  },
  allow_camera_prompt: {
    en: "When prompted, you need to allow camera access to continue.",
    ar: "عند المطالبة، تحتاج إلى السماح بالوصول للكاميرا للمتابعة.",
  },
  photo: {
    en: "Photo",
    ar: "صورة",
  },
  of: {
    en: "of",
    ar: "من",
  },
  // Choose Doctor Procedure translations
  choose_doctor_procedure: {
    en: "Choose Doctor Procedure",
    ar: "اختر إجراء الطبيب",
  },
  select_procedure_type: {
    en: "Select the type of procedure you want to perform",
    ar: "اختر نوع الإجراء الذي تريد القيام به",
  },
  open_clinic: {
    en: "Open Clinic",
    ar: "فتح عيادة",
  },
  open_clinic_desc: {
    en: "Opening a new medical clinic",
    ar: "فتح عيادة طبية جديدة",
  },
  register_existing_clinic: {
    en: "Register Existing Clinic",
    ar: "تسجيل عيادة موجودة",
  },
  register_existing_clinic_desc: {
    en: "Registering an already established clinic",
    ar: "تسجيل عيادة موجودة مسبقاً",
  },
  transfer_clinic: {
    en: "Transfer Clinic",
    ar: "نقل عيادة",
  },
  transfer_clinic_desc: {
    en: "Transferring clinic ownership",
    ar: "نقل ملكية العيادة",
  },
  close_clinic: {
    en: "Close Clinic",
    ar: "إغلاق عيادة",
  },
  close_clinic_desc: {
    en: "Closing an existing clinic",
    ar: "إغلاق عيادة موجودة",
  },
  loading: {
    en: "Loading...",
    ar: "جاري التحميل...",
  },
  loading_clinic_info: {
    en: "Loading clinic information...",
    ar: "جاري تحميل معلومات العيادة...",
  },
  loading_degree_info: {
    en: "Loading degree information...",
    ar: "جاري تحميل معلومات الشهادة...",
  },
  loading_doctor_info: {
    en: "Loading doctor information...",
    ar: "جاري تحميل معلومات الطبيب...",
  },
  loading_procedure_info: {
    en: "Loading procedure information...",
    ar: "جاري تحميل معلومات الإجراء...",
  },
  enter_place_of_graduation: {
    en: "Enter place of graduation",
    ar: "أدخل مكان التخرج",
  },
  loading1: {
    en: "Loading",
    ar: "جاري التحميل",
  },
  continue_procedure: {
    en: "Continue",
    ar: "متابعة",
  },
  // Doctor Info Registration translations
  doctor_information: {
    en: "Doctor Information",
    ar: "معلومات الطبيب",
  },
  full_name: {
    en: "Full Name",
    ar: "الاسم الكامل",
  },
  confirm_information: {
    en: "Confirm Information",
    ar: "تأكيد المعلومات",
  },
  please_verify_details: {
    en: "Please verify the details below",
    ar: "يرجى التحقق من التفاصيل أدناه",
  },
  personal_work_info: {
    en: "Personal and Work Information",
    ar: "المعلومات الشخصية والمهنية",
  },
  personal_address: {
    en: "Personal Address",
    ar: "العنوان الشخصي",
  },
  select_governorate: {
    en: "Select Governorate",
    ar: "اختر المحافظة",
  },
  district: {
    en: "District",
    ar: "القضاء",
  },
  sub_district: {
    en: "Sub District",
    ar: "الناحية",
  },
  alley: {
    en: "Alley",
    ar: "الزقاق",
  },
  neighborhood: {
    en: "Neighborhood",
    ar: "المحلة",
  },
  enter_neighborhood: {
    en: "Enter Neighborhood",
    ar: "أدخل المحلة",
  },
  house_number: {
    en: "House Number",
    ar: "رقم الدار",
  },
  dob: {
    en: "Date of Birth",
    ar: "تاريخ الميلاد",
  },
  enter_dob: {
    en: "Select Date of Birth",
    ar: "اختر تاريخ الميلاد",
  },
  personal_details: {
    en: "Personal Details",
    ar: "التفاصيل الشخصية",
  },
  sex: {
    en: "Sex",
    ar: "الجنس",
  },
  male: {
    en: "Male",
    ar: "ذكر",
  },
  female: {
    en: "Female",
    ar: "أنثى",
  },
  other: {
    en: "Other",
    ar: "آخر",
  },
  work_information: {
    en: "Work Information",
    ar: "معلومات العمل",
  },
  work_sector: {
    en: "Work Sector",
    ar: "قطاع العمل",
  },
  private_sector: {
    en: "Private Sector",
    ar: "القطاع الخاص",
  },
  government_ministry: {
    en: "Government Ministry",
    ar: "وزارة حكومية",
  },
  select_ministry: {
    en: "Select Ministry",
    ar: "اختر الوزارة",
  },
  department: {
    en: "Department",
    ar: "الدائرة",
  },
  university: {
    en: "University",
    ar: "الجامعة",
  },
  college: {
    en: "College",
    ar: "الكلية",
  },
  hospital: {
    en: "Hospital",
    ar: "مستشفى",
  },
  select_medical_career: {
    en: "Select Medical Career",
    ar: "اختر الدرجة المهنية",
  },
  governorate: {
    en: "Governorate",
    ar: "المحافظة",
  },
  select_department: {
    en: "Select Department",
    ar: "اختر الدائرة",
  },
  select_hospital: {
    en: "Select Hospital",
    ar: "اختر المستشفى",
  },
  select_college: {
    en: "Select College",
    ar: "اختر الكلية",
  },
  medical_career: {
    en: "Medical Career",
    ar: "الدرجة المهنية",
  },
  // Employment Status translations
  employment_status: {
    en: "Employment Status",
    ar: "حالة التوظيف",
  },
  select_employment_status: {
    en: "Select Employment Status",
    ar: "اختر حالة التوظيف",
  },
  hospital_employee: {
    en: "Hospital Employee",
    ar: "موظف مستشفى",
  },
  health_center_employee: {
    en: "Health Center Employee",
    ar: "موظف مركز صحي",
  },
  sector_employee: {
    en: "Sector Employee",
    ar: "موظف قطاع",
  },
  university_employee: {
    en: "University Employee",
    ar: "موظف جامعة",
  },
  organization_employee: {
    en: "Organization Employee",
    ar: "موظف دائرة",
  },
  retired: {
    en: "Retired",
    ar: "متقاعد",
  },
  resigned: {
    en: "Resigned",
    ar: "مستقيل",
  },
  fired: {
    en: "Fired",
    ar: "مفصول",
  },
  // Sector and Health Center translations
  sector: {
    en: "Sector",
    ar: "القطاع الصحي",
  },
  search_select_sector: {
    en: "Search and select sector",
    ar: "ابحث واختر القطاع الصحي",
  },
  health_center: {
    en: "Health Center",
    ar: "المركز الصحي",
  },
  search_select_health_center: {
    en: "Search and select health center",
    ar: "ابحث واختر المركز الصحي",
  },
  ministry: {
    en: "Ministry",
    ar: "الوزارة",
  },
  workplace_location: {
    en: "Workplace Location",
    ar: "مكان العمل",
  },
  workplace_type: {
    en: "Workplace Type",
    ar: "نوع مكان العمل",
  },
  university_college: {
    en: "University / College",
    ar: "جامعة / كلية",
  },
  or: {
    en: "OR",
    ar: "أو",
  },
  selecting_this_will_clear_university_college: {
    en: "Selecting a hospital will clear university and college",
    ar: "اختيار المستشفى سيمسح الجامعة والكلية",
  },
  selecting_this_will_clear_hospital: {
    en: "Selecting university or college will clear hospital",
    ar: "اختيار الجامعة أو الكلية سيمسح المستشفى",
  },
  // Ministry values
  health: {
    en: "Ministry of Health",
    ar: "وزارة الصحة",
  },
  education: {
    en: "Ministry of Education",
    ar: "وزارة التربية",
  },
  higher_education: {
    en: "Ministry of Higher Education",
    ar: "وزارة التعليم العالي",
  },
  defense: {
    en: "Ministry of Defense",
    ar: "وزارة الدفاع",
  },
  interior: {
    en: "Ministry of Interior",
    ar: "وزارة الداخلية",
  },
  finance: {
    en: "Ministry of Finance",
    ar: "وزارة المالية",
  },
  foreign_affairs: {
    en: "Ministry of Foreign Affairs",
    ar: "وزارة الخارجية",
  },
  justice: {
    en: "Ministry of Justice",
    ar: "وزارة العدل",
  },
  oil: {
    en: "Ministry of Oil",
    ar: "وزارة النفط",
  },
  electricity: {
    en: "Ministry of Electricity",
    ar: "وزارة الكهرباء",
  },
  water_resources: {
    en: "Ministry of Water Resources",
    ar: "وزارة الموارد المائية",
  },
  agriculture: {
    en: "Ministry of Agriculture",
    ar: "وزارة الزراعة",
  },
  trade: {
    en: "Ministry of Trade",
    ar: "وزارة التجارة",
  },
  industry: {
    en: "Ministry of Industry",
    ar: "وزارة الصناعة",
  },
  transportation: {
    en: "Ministry of Transportation",
    ar: "وزارة النقل",
  },
  communications: {
    en: "Ministry of Communications",
    ar: "وزارة الاتصالات",
  },
  labor_social_affairs: {
    en: "Ministry of Labor & Social Affairs",
    ar: "وزارة العمل والشؤون الاجتماعية",
  },
  migration_displacement: {
    en: "Ministry of Migration & Displacement",
    ar: "وزارة الهجرة والمهجرين",
  },
  youth_sports: {
    en: "Ministry of Youth & Sports",
    ar: "وزارة الشباب والرياضة",
  },
  culture: {
    en: "Ministry of Culture",
    ar: "وزارة الثقافة",
  },
  tourism_antiquities: {
    en: "Ministry of Tourism & Antiquities",
    ar: "وزارة السياحة والآثار",
  },
  housing_construction: {
    en: "Ministry of Housing & Construction",
    ar: "وزارة الإسكان والتعمير",
  },
  planning: {
    en: "Ministry of Planning",
    ar: "وزارة التخطيط",
  },
  environment: {
    en: "Ministry of Environment",
    ar: "وزارة البيئة",
  },
  science_technology: {
    en: "Ministry of Science & Technology",
    ar: "وزارة العلوم والتكنولوجيا",
  },
  municipalities: {
    en: "Ministry of Municipalities",
    ar: "وزارة البلديات",
  },
  optional_for_private: {
    en: "Optional for Private Practice",
    ar: "اختياري للممارسة الخاصة",
  },
  private_practice: {
    en: "Private Practice",
    ar: "ممارسة خاصة",
  },
  enter_department: {
    en: "Enter Department",
    ar: "أدخل الدائرة",
  },
  enter_hospital: {
    en: "Enter Hospital",
    ar: "أدخل المستشفى",
  },
  enter_university: {
    en: "Enter University",
    ar: "أدخل الجامعة",
  },
  enter_college: {
    en: "Enter College",
    ar: "أدخل الكلية",
  },
  select_sub_specialty: {
    en: "Select Sub-specialty",
    ar: "حدد التخصص الدقيق",
  },
  enter_union_order_number: {
    en: "Enter union order number",
    ar: "أدخل رقم الأمر النقابي الخاص بتسجيل لقب الاختصاص",
  },
  // Degree Specialties translations
  education_qualifications: {
    en: "Education & Qualifications",
    ar: "التعليم والمؤهلات",
  },
  degree_specialties_info: {
    en: "Degree and Specialties Information",
    ar: "معلومات الشهادة والتخصصات",
  },
  education_details: {
    en: "Education Details",
    ar: "تفاصيل التعليم",
  },
  place_of_graduation: {
    en: "Place of Graduation",
    ar: "مكان التخرج",
  },
  select_university: {
    en: "Select University/Institution",
    ar: "اختر الجامعة/الدائرة",
  },
  graduation_date: {
    en: "Graduation Date",
    ar: "تاريخ التخرج",
  },
  degree: {
    en: "Degree",
    ar: "الشهادة",
  },
  select_degree: {
    en: "Select Degree",
    ar: "اختر الشهادة",
  },
  specialization: {
    en: "Specialization",
    ar: "التخصص",
  },
  primary_specialty: {
    en: "Primary Specialty",
    ar: "التخصص الرئيسي",
  },
  select_specialty: {
    en: "Select Specialty",
    ar: "اختر التخصص",
  },
  sub_specialty_optional: {
    en: "Sub-Specialty (Optional)",
    ar: "التخصص الدقيق (اختياري)",
  },
  professional_details: {
    en: "Professional Details",
    ar: "التفاصيل المهنية",
  },
  union_order_number: {
    en: "Union Order Number",
    ar: "رقم الأمر النقابي الخاص بتسجيل لقب الاختصاص",
  },

  // Clinic Info Registration translations
  clinic_information: {
    en: "Clinic Information",
    ar: "معلومات العيادة",
  },
  clinic_location_operational: {
    en: "Clinic Location and Operational Details",
    ar: "موقع العيادة والتفاصيل التشغيلية",
  },
  clinic_address: {
    en: "Clinic Address",
    ar: "عنوان العيادة",
  },
  location_coordinates: {
    en: "Location Coordinates",
    ar: "إحداثيات الموقع",
  },
  get_current_location: {
    en: "Get Current Location",
    ar: "احصل على الموقع الحالي",
  },
  getting_location: {
    en: "Getting Location...",
    ar: "جاري الحصول على الموقع...",
  },
  select_location: {
    en: "Select Location",
    ar: "اختر الموقع",
  },
  select_location_on_map: {
    en: "Select Location on Map",
    ar: "اختر الموقع على الخريطة",
  },
  use_current_location: {
    en: "Use Current Location",
    ar: "استخدم الموقع الحالي",
  },
  clear_location: {
    en: "Clear",
    ar: "مسح",
  },
  selected_location: {
    en: "Selected Location",
    ar: "الموقع المحدد",
  },
  fetching_address: {
    en: "Fetching address...",
    ar: "جاري جلب العنوان...",
  },
  location_picker_instruction: {
    en: "Click on the map to select a location, or use the GPS button to get your current location.",
    ar: "انقر على الخريطة لتحديد الموقع، أو استخدم زر GPS للحصول على موقعك الحالي.",
  },
  location_required: {
    en: "Location selection is required to continue",
    ar: "تحديد الموقع مطلوب للمتابعة",
  },
  city_required: {
    en: "Please select a city",
    ar: "يرجى اختيار المدينة",
  },
  room_photo: {
    en: "Room Photo",
    ar: "صورة الغرفة",
  },
  no_active_registration: {
    en: "No active registration found.",
    ar: "لا يوجد طلب تسجيل نشط.",
  },
  no_active_closure: {
    en: "No active closure request found.",
    ar: "لا يوجد طلب غلق نشط.",
  },
  clinic_location: {
    en: "Clinic Location",
    ar: "موقع العيادة",
  },
  location_selected: {
    en: "Location Selected",
    ar: "تم تحديد الموقع",
  },
  click_to_change: {
    en: "Click to change location",
    ar: "انقر لتغيير الموقع",
  },
  change_location: {
    en: "Change Location",
    ar: "تغيير الموقع",
  },
  choose_location: {
    en: "Choose Location",
    ar: "اختر الموقع",
  },
  or_enter_manually: {
    en: "or enter manually below",
    ar: "أو أدخلها يدوياً أدناه",
  },
  latitude: {
    en: "Latitude",
    ar: "خط العرض",
  },
  longitude: {
    en: "Longitude",
    ar: "خط الطول",
  },
  language_code: {
    en: "en",
    ar: "ar",
  },
  geolocation_not_supported: {
    en: "Geolocation is not supported by your browser",
    ar: "الموقع الجغرافي غير مدعوم من قبل متصفحك",
  },
  location_error: {
    en: "Error getting your location",
    ar: "خطأ في الحصول على موقعك",
  },
  location_permission_denied: {
    en: "Location access was denied. Please enable location permissions.",
    ar: "تم رفض الوصول إلى الموقع. يرجى تمكين أذونات الموقع.",
  },
  location_unavailable: {
    en: "Location information is unavailable.",
    ar: "معلومات الموقع غير متوفرة.",
  },
  location_timeout: {
    en: "Location request timed out.",
    ar: "انتهت مهلة طلب الموقع.",
  },
  contact_information: {
    en: "Contact Information",
    ar: "معلومات الاتصال",
  },
  working_hours: {
    en: "Working Hours",
    ar: "ساعات العمل",
  },
  select_working_hours: {
    en: "Select Working Hours",
    ar: "اختر ساعات العمل",
  },
  business_information: {
    en: "Business Information",
    ar: "معلومات العمل",
  },
  tax_number_optional: {
    en: "Tax Number (Optional)",
    ar: "الرقم الضريبي (اختياري)",
  },
  tax_number: {
    en: "Tax Number",
    ar: "الرقم الضريبي",
  },
  has_other_clinic: {
    en: "I have another clinic",
    ar: "لدي عيادة أخرى",
  },
  supervises_medical_center: {
    en: "I supervise a medical center",
    ar: "أشرف على مركز طبي",
  },
  house_building: {
    en: "House/Building",
    ar: "المنزل/المبنى",
  },
  nearest_landmark_optional: {
    en: "Nearest Landmark (Optional)",
    ar: "أقرب نقطة دالة (اختياري)",
  },
  // Document Upload translations
  document_upload: {
    en: "Document Upload",
    ar: "رفع الوثائق",
  },
  upload_required_documents: {
    en: "Upload Required Documents",
    ar: "رفع الوثائق المطلوبة",
  },
  required_documents: {
    en: "Required Documents",
    ar: "الوثائق المطلوبة",
  },
  optional_documents: {
    en: "Optional Documents",
    ar: "الوثائق الاختيارية",
  },
  progress: {
    en: "Progress",
    ar: "التقدم",
  },
  required_documents_uploaded: {
    en: "required documents uploaded",
    ar: "وثائق مطلوبة تم رفعها",
  },
  total_uploaded: {
    en: "Total uploaded",
    ar: "المجموع المرفوع",
  },
  documents: {
    en: "documents",
    ar: "وثائق",
  },
  required: {
    en: "Required",
    ar: "مطلوب",
  },
  upload: {
    en: "Upload",
    ar: "رفع",
  },
  replace: {
    en: "Replace",
    ar: "استبدال",
  },
  remove: {
    en: "Remove",
    ar: "إزالة",
  },
  uploading: {
    en: "Uploading...",
    ar: "جاري الرفع...",
  },
  uploading_documents: {
    en: "Uploading Documents",
    ar: "جاري رفع الوثائق",
  },
  please_wait_uploading: {
    en: "Please wait while we upload your documents...",
    ar: "يرجى الانتظار بينما نقوم برفع وثائقك...",
  },
  yes: {
    en: "Yes",
    ar: "نعم",
  },
  no: {
    en: "No",
    ar: "لا",
  },
  describe_clinic_location_on_map: {
    en: "Describe clinic location on map",
    ar: "أضف موقع العيادة على الخريطة",
  },
  coordinates: {
    en: "Coordinates",
    ar: "الإحداثيات",
  },
  confirm: {
    en: "Confirm",
    ar: "تأكيد",
  },
  select_current_location: {
    en: "Select current location",
    ar: "اختر الموقع الحالي",
  },
  current_location: {
    en: "Current Location",
    ar: "الموقع الحالي",
  },
  nearest_landmark: {
    en: "Nearest Landmark",
    ar: "أقرب نقطة دالة",
  },
  custom_working_hours: {
    en: "Enter custom working hours",
    ar: "أدخل ساعات العمل المخصصة",
  },
  optional: {
    en: "Optional",
    ar: "اختياري",
  },
  clinic_coordinates: {
    en: "Clinic Coordinates",
    ar: "إحداثيات العيادة",
  },
  document_status: {
    en: "Document Status",
    ar: "حالة الوثائق",
  },
  missing_required_documents: {
    en: "Missing Required Documents",
    ar: "الوثائق المطلوبة المفقودة",
  },
  specialist_certificate_requirement: {
    en: "Specialist Requirement: You must upload Union Order Certificate for Specialty Title Registration",
    ar: "متطلبات الأخصائي: يجب رفع شهادة الامر النقابي بتسجيل لقب الاختصاص",
  },
  all_required_documents_uploaded: {
    en: "All required documents uploaded successfully!",
    ar: "تم رفع جميع الوثائق المطلوبة بنجاح!",
  },
  validating: {
    en: "Validating",
    ar: "جاري التحقق",
  },
  missing: {
    en: "missing",
    ar: "مفقود",
  },
  click_continue_to_proceed: {
    en: "Click Continue to proceed to the next step",
    ar: "انقر متابعة للانتقال إلى الخطوة التالية",
  },
  api_validation_missing: {
    en: "API Validation - Missing Documents",
    ar: "التحقق من الخادم - الوثائق المفقودة",
  },
  api_validation_passed: {
    en: "API validation passed successfully",
    ar: "تم التحقق من الخادم بنجاح",
  },
  delete_file: {
    en: "Delete file",
    ar: "حذف الملف",
  },
  drop_files_or_click: {
    en: "Drop files here or click to browse",
    ar: "اسحب الملفات هنا أو انقر للتصفح",
  },
  specialist_certificates: {
    en: "Specialist Certificates",
    ar: "شهادات الأخصائي",
  },
  upload_one_required: {
    en: "Upload One Required",
    ar: "رفع واحدة مطلوب",
  },
  specialist_certificate_note: {
    en: "For specialist doctors, you must upload Union Order Certificate for Specialty Title Registration.",
    ar: "للأطباء الأخصائيين، يجب رفع شهادة الامر النقابي بتسجيل لقب الاختصاص.",
  },
  specialist_requirement_satisfied: {
    en: "Specialist requirement satisfied",
    ar: "تم استيفاء متطلبات الأخصائي",
  },
  PersonalPhoto: {
    en: "Personal Photo",
    ar: "الصورة الشخصية",
  },
  IraqiCivilIdFront: {
    en: "Iraqi Civil ID Front",
    ar: "الوجه الأمامي للهوية المدنية العراقية",
  },
  IraqiCivilIdBack: {
    en: "Iraqi Civil ID Back",
    ar: "الوجه الخلفي للهوية المدنية العراقية",
  },
  GraduationCertificate: {
    en: "Graduation Certificate",
    ar: "شهادة التخرج",
  },
  ResidencyCompletionCertificate: {
    en: "Residency Completion Certificate",
    ar: "شهادة إكمال الاقامة",
  },
  HigherEducationCertificate: {
    en: "Higher Education Certificate",
    ar: "شهادة التعليم العالي",
  },
  UnionOrderCertificate: {
    en: "Union Order Certificate for Specialty Title Registration",
    ar: "شهادة الامر النقابي بتسجيل لقب الاختصاص",
  },
  ClinicSignPhoto: {
    en: "Clinic Sign Photo",
    ar: "صورة لافتة العيادة",
  },
  ClinicRoomsPhotos: {
    en: "Clinic Rooms Photos",
    ar: "صور غرف العيادة",
  },
  ClinicOpeningCertificate: {
    en: "Clinic Opening Certificate",
    ar: "شهادة افتتاح العيادة",
  },
  ClinicOwnershipProof: {
    en: "Confirmation of Not Owning a Clinic in Residence Governorate",
    ar: "تاييد عدم امتلاك عيادة في محافظة السكن",
  },
  ExternalIdentificationPlate: {
    en: "External Identification Plate",
    ar: "اللوحة التعريفية الخارجية",
  },
  InternalIdentificationPlate: {
    en: "Internal Identification Plate",
    ar: "اللوحة التعريفية الداخلية",
  },
  PrescriptionPhoto: {
    en: "Prescription Photo",
    ar: "صورة الوصفة الطبية",
  },

  // Success Screen Messages
  application_sent_successfully: {
    en: "Your application sent successfully",
    ar: "تم ارسال طلبك بنجاح",
  },
  iraqi_medical_association_review: {
    en: "Your application will be reviewed and you will be notified of the result by the Iraqi Medical Association as soon as possible",
    ar: "سيتم مراجعة طلبك واعلامك بالنتيجة من قبل نقابة اطباء العراق في اقرب وقت",
  },
  inspector_will_contact: {
    en: "One of our inspectors will contact you as soon as possible",
    ar: "سيقوم احد مفتشينا بالاتصال بك في اسرع وقت",
  },
  inspector_info: {
    en: "Inspector Information",
    ar: "معلومات المفتش",
  },
  inspector_name: {
    en: "Inspector Name",
    ar: "اسم المفتش",
  },
  inspector_phone: {
    en: "Inspector Phone",
    ar: "هاتف المفتش",
  },
  thank_you_time: {
    en: "Thank you for your time",
    ar: "شاكرين وقتك",
  },
  success_open_new_clinic: {
    en: "Open New Clinic",
    ar: "فتح عيادة جديدة",
  },
  success_register_existing_clinic: {
    en: "Register Existing Clinic",
    ar: "تسجيل عيادة موجودة",
  },
  success_application_submitted: {
    en: "Application submitted successfully",
    ar: "تم إرسال الطلب بنجاح",
  },
  success_all_documents_complete: {
    en: "All documents complete",
    ar: "جميع الوثائق مكتملة",
  },
  success_fees_processed: {
    en: "Fees processed",
    ar: "تم إستلام الرسوم",
  },
  PracticeReferralLetter: {
    en: "Practice Referral Letter",
    ar: "كتاب إحالة ممارسة",
  },
  personal_documents: {
    en: "Personal Documents",
    ar: "المرفقات الشخصية",
  },
  clinic_documents: {
    en: "Clinic Documents",
    ar: "مرفقات العيادة",
  },
  // Personal Document Types
  "Iraqi Doctors Syndicate ID Front": {
    en: "Iraqi Doctors Syndicate ID Front",
    ar: "هوية نقابة الأطباء العراقية - الوجه الأمامي",
  },
  "Iraqi Doctors Syndicate ID Back": {
    en: "Iraqi Doctors Syndicate ID Back",
    ar: "هوية نقابة الأطباء العراقية - الوجه الخلفي",
  },
  "Iraqi Unified ID Front": {
    en: "Iraqi Unified ID Front",
    ar: "الهوية الموحدة العراقية - الوجه الأمامي",
  },
  "Iraqi Unified ID Back": {
    en: "Iraqi Unified ID Back",
    ar: "الهوية الموحدة العراقية - الوجه الخلفي",
  },
  "Residency Card Front": {
    en: "Residency Card Front",
    ar: "بطاقة السكن - الوجه الأمامي",
  },
  "Residency Card Back": {
    en: "Residency Card Back",
    ar: "بطاقة السكن - الوجه الخلفي",
  },

  "Practice Referral Letter": {
    en: "Practice Referral Letter",
    ar: "كتاب إحالة الممارسة",
  },
  // Clinic Document Types
  "External Identification Plate Photo": {
    en: "External Identification Plate Photo",
    ar: "صورة لوحة التعريف الخارجية",
  },
  "Internal Identification Plate Photo": {
    en: "Internal Identification Plate Photo",
    ar: "صورة لوحة التعريف الداخلية",
  },

  "Prescription Photo": {
    en: "Prescription Photo",
    ar: "صورة الوصفة الطبية",
  },
  "Clinic Ownership Proof (if outside residence governorate)": {
    en: "Clinic Ownership Proof (if outside residence governorate)",
    ar: "إثبات ملكية العيادة (إذا كانت خارج محافظة الإقامة)",
  },
  "Lease Contract": {
    en: "Lease Contract",
    ar: "عقد الإيجار",
  },

  // Iraqi Governorates
  "Al Anbar": {
    en: "Al Anbar",
    ar: "الأنبار",
  },
  "Al Muthanna": {
    en: "Al Muthanna",
    ar: "المثنى",
  },
  "Al Qadisiyah": {
    en: "Al Qadisiyah",
    ar: "القادسية",
  },
  Babylon: {
    en: "Babylon",
    ar: "بابل",
  },
  Baghdad: {
    en: "Baghdad",
    ar: "بغداد",
  },
  Basrah: {
    en: "Basrah",
    ar: "البصرة",
  },
  Dhiqar: {
    en: "Dhiqar",
    ar: "ذي قار",
  },
  Diyala: {
    en: "Diyala",
    ar: "ديالى",
  },
  Duhok: {
    en: "Duhok",
    ar: "دهوك",
  },
  Erbil: {
    en: "Erbil",
    ar: "أربيل",
  },
  Halabja: {
    en: "Halabja",
    ar: "حلبجة",
  },
  Karbala: {
    en: "Karbala",
    ar: "كربلاء",
  },
  Kirkuk: {
    en: "Kirkuk",
    ar: "كركوك",
  },
  Maysan: {
    en: "Maysan",
    ar: "ميسان",
  },
  Najaf: {
    en: "Najaf",
    ar: "النجف",
  },
  Nineveh: {
    en: "Nineveh",
    ar: "نينوى",
  },
  Saladin: {
    en: "Saladin",
    ar: "صلاح الدين",
  },
  Sulaymaniyah: {
    en: "Sulaymaniyah",
    ar: "السليمانية",
  },
  Wasit: {
    en: "Wasit",
    ar: "واسط",
  },

  // Iraqi Ministries
  "Ministry of Health": {
    en: "Ministry of Health",
    ar: "وزارة الصحة",
  },
  "Ministry of Education": {
    en: "Ministry of Education",
    ar: "وزارة التربية",
  },
  "Ministry of Higher Education and Scientific Research": {
    en: "Ministry of Higher Education and Scientific Research",
    ar: "وزارة التعليم العالي والبحث العلمي",
  },
  "Ministry of Defense": {
    en: "Ministry of Defense",
    ar: "وزارة الدفاع",
  },
  "Ministry of Interior": {
    en: "Ministry of Interior",
    ar: "وزارة الداخلية",
  },
  "Ministry of Justice": {
    en: "Ministry of Justice",
    ar: "وزارة العدل",
  },
  "Ministry of Foreign Affairs": {
    en: "Ministry of Foreign Affairs",
    ar: "وزارة الخارجية",
  },
  "Ministry of Finance": {
    en: "Ministry of Finance",
    ar: "وزارة المالية",
  },
  "Ministry of Oil": {
    en: "Ministry of Oil",
    ar: "وزارة النفط",
  },
  "Ministry of Electricity": {
    en: "Ministry of Electricity",
    ar: "وزارة الكهرباء",
  },

  // Medical Career Enum Values
  PRACTICING_DOCTOR: {
    en: "Practicing Doctor",
    ar: "طبيب ممارس",
  },
  SPECIALIST_DOCTOR: {
    en: "Specialist Doctor",
    ar: "طبيب أخصائي",
  },

  // Document type keys without spaces
  IraqiDoctorsSyndicateIdFront: {
    en: "Iraqi Doctors Syndicate ID Front",
    ar: "هوية نقابة الأطباء العراقية - الوجه الأمامي",
  },
  IraqiDoctorsSyndicateIdBack: {
    en: "Iraqi Doctors Syndicate ID Back",
    ar: "هوية نقابة الأطباء العراقية - الوجه الخلفي",
  },
  IraqiUnifiedIdFront: {
    en: "Iraqi Unified ID Front",
    ar: "الهوية الموحدة العراقية - الوجه الأمامي",
  },
  IraqiUnifiedIdBack: {
    en: "Iraqi Unified ID Back",
    ar: "الهوية الموحدة العراقية - الوجه الخلفي",
  },
  ResidencyCardFront: {
    en: "Residency Card Front",
    ar: "بطاقة السكن - الوجه الأمامي",
  },
  ResidencyCardBack: {
    en: "Residency Card Back",
    ar: "بطاقة السكن - الوجه الخلفي",
  },

  // File upload text
  max_files: {
    en: "Max {count} files",
    ar: "حد أقصى {count} ملفات",
  },
  file_types_and_size: {
    en: "Photos (JPG, PNG, WEBP, HEIC) or PDF • up to 10 MB each",
    ar: "صور (JPG أو PNG أو WEBP أو HEIC) أو PDF • حتى 10 ميجابايت لكل ملف",
  },
  invalid_file_type: {
    en: "Only photos (JPG, PNG, WEBP, HEIC) and PDF files are accepted",
    ar: "تُقبل الصور (JPG أو PNG أو WEBP أو HEIC) وملفات PDF فقط",
  },
  file_too_large: {
    en: "File size must be less than 10MB",
    ar: "يجب أن يكون حجم الملف أقل من 10 ميجابايت",
  },
  specialist_requirement: {
    en: "Specialist Requirement",
    ar: "متطلبات الأخصائي",
  },
  specialist_requirement_text: {
    en: "You must upload Union Order Certificate for Specialty Title Registration",
    ar: "يجب رفع شهادة الامر النقابي بتسجيل لقب الاختصاص",
  },
  all_documents_uploaded: {
    en: "All required documents uploaded successfully",
    ar: "تم رفع جميع المستندات المطلوبة بنجاح",
  },

  // Practitioner Level Enum Values
  "Medical Student": {
    en: "Medical Student",
    ar: "طالب طب",
  },
  Intern: {
    en: "Intern",
    ar: "طبيب امتياز",
  },
  "Junior Resident": {
    en: "Junior Resident",
    ar: "مقيم أول",
  },
  "Senior Resident": {
    en: "Senior Resident",
    ar: "مقيم أقدم",
  },
  "Practicing Physician": {
    en: "Practicing Physician",
    ar: "طبيب ممارس",
  },
  "Senior Practitioner": {
    en: "Senior Practitioner",
    ar: "ممارس أقدم",
  },
  Specialist: {
    en: "Specialist",
    ar: "أخصائي",
  },
  Consultant: {
    en: "Consultant",
    ar: "استشاري",
  },
  "Chief Physician": {
    en: "Chief Physician",
    ar: "طبيب رئيس",
  },
  "Professor Practitioner": {
    en: "Professor Practitioner",
    ar: "أستاذ ممارس",
  },
  Emeritus: {
    en: "Emeritus",
    ar: "فخري",
  },

  // Career guidance text
  specialist_career_guidance: {
    en: "As a specialist, you must provide union order number. Sub-specialty is optional.",
    ar: "كطبيب أخصائي، يجب أن تقدم رقم الأمر النقابي الخاص بتسجيل لقب الاختصاص. التخصص الدقيق اختياري.",
  },
  practicing_career_guidance: {
    en: "As a practicing doctor, you must provide practitioner level and can only hold MBChB degree.",
    ar: "كطبيب ممارس، يجب أن تقدم مستوى الممارسة ويمكنك فقط حمل درجة بكالوريوس الطب والجراحة العامة.",
  },

  // Missing translation keys
  degree_specialties: {
    en: "Degree & Specialties",
    ar: "الشهادة والتخصصات",
  },
  degree_specialties_subtitle: {
    en: "Enter your education and specialization details",
    ar: "أدخل تفاصيل التعليم والتخصص",
  },
  specialty: {
    en: "Specialty",
    ar: "التخصص",
  },
  practicing_doctor: {
    en: "Practicing Doctor",
    ar: "طبيب ممارس",
  },
  practicing_doctor_requirements: {
    en: "As a practicing doctor, you must provide practitioner level and can only hold MBChB degree.",
    ar: "كطبيب ممارس، يجب أن تقدم مستوى الممارسة ويمكنك فقط حمل درجة بكالوريوس الطب والجراحة العامة.",
  },
  sub_specialty: {
    en: "Sub-specialty",
    ar: "التخصص الدقيق",
  },
  specialist_doctor: {
    en: "Specialist Doctor",
    ar: "طبيب أخصائي",
  },
  specialist_doctor_requirements: {
    en: "As a specialist, you must provide union order number. Sub-specialty is optional.",
    ar: "كطبيب أخصائي، يجب أن تقدم رقم الأمر النقابي الخاص بتسجيل لقب الاختصاص. التخصص الدقيق اختياري.",
  },
  // Form Field Translations
  email: {
    en: "Email",
    ar: "البريد الإلكتروني",
  },
  date_of_birth: {
    en: "Date of Birth",
    ar: "تاريخ الميلاد",
  },
  gender: {
    en: "Gender",
    ar: "الجنس",
  },
  select_gender: {
    en: "Select Gender",
    ar: "اختر الجنس",
  },
  language: {
    en: "Language",
    ar: "اللغة",
  },
  select_language: {
    en: "Select Language",
    ar: "اختر اللغة",
  },
  medical_license_number: {
    en: "Medical License Number",
    ar: "رقم الهوية النقابية",
  },
  license_issued_date: {
    en: "License Issued Date",
    ar: "تاريخ إصدار التسجيل",
  },
  license_expiry_date: {
    en: "License Expiry Date",
    ar: "تاريخ انتهاء التسجيل",
  },
  career_progression: {
    en: "Professional Level",
    ar: "المستوى المهني",
  },
  select_career_progression: {
    en: "Select Professional Level",
    ar: "اختر المستوى المهني",
  },
  years_of_experience: {
    en: "Years of Experience",
    ar: "سنوات الخبرة",
  },
  graduation_year: {
    en: "Graduation Year",
    ar: "سنة التخرج",
  },
  medical_degree: {
    en: "Medical Degree",
    ar: "الشهادة الطبية",
  },
  select_medical_degree: {
    en: "Select Medical Degree",
    ar: "اختر الشهادة الطبية",
  },
  additional_qualifications: {
    en: "Additional Qualifications",
    ar: "المؤهلات الإضافية",
  },
  association_specialization_order_number: {
    en: "Association Order Number",
    ar: "رقم الأمر النقابي",
  },
  city: {
    en: "City",
    ar: "المدينة",
  },
  street: {
    en: "Street",
    ar: "الزقاق",
  },
  personal_information: {
    en: "Personal Information",
    ar: "المعلومات الشخصية",
  },
  medical_license_info: {
    en: "Medical License Information",
    ar: "معلومات التسجيل الطبية",
  },
  professional_information: {
    en: "Professional Information",
    ar: "المعلومات المهنية",
  },
  address_information: {
    en: "Address Information",
    ar: "معلومات العنوان",
  },
  education_information: {
    en: "Education Information",
    ar: "المعلومات التعليمية",
  },
  career_information: {
    en: "Career Information",
    ar: "المعلومات المهنية",
  },
  // Validation Messages
  field_required: {
    en: "This field is required",
    ar: "هذا الحقل مطلوب",
  },
  invalid_email: {
    en: "Please enter a valid email address",
    ar: "يرجى إدخال عنوان بريد إلكتروني صحيح",
  },
  min_age_24: {
    en: "Doctor must be at least 24 years old",
    ar: "يجب أن يكون عمر الطبيب 24 سنة على الأقل",
  },
  year_must_be_1940_or_later: {
    en: "Year must be 1940 or later",
    ar: "يجب أن تكون السنة 1940 أو أحدث",
  },
  invalid_graduation_year: {
    en: "Please enter a valid graduation year",
    ar: "يرجى إدخال سنة تخرج صحيحة",
  },
  negative_experience: {
    en: "Years of experience cannot be negative",
    ar: "لا يمكن أن تكون سنوات الخبرة سالبة",
  },
  positive_number_required: {
    en: "Please enter a positive number",
    ar: "يرجى إدخال رقم موجب",
  },

  // Clinic Registration Form Fields
  doctor_full_name: {
    en: "Doctor Full Name",
    ar: "الاسم الكامل للطبيب",
  },
  graduation_place: {
    en: "Graduation Place",
    ar: "مكان التخرج",
  },
  syndicate_registration_number: {
    en: "Syndicate Registration Number",
    ar: "رقم التسجيل في النقابة",
  },
  specialization_registration_number: {
    en: "Specialization Registration Number",
    ar: "رقم تسجيل التخصص",
  },
  specialization_field: {
    en: "Specialization Field",
    ar: "مجال التخصص",
  },
  specialization_degree: {
    en: "Specialization Degree",
    ar: "درجة التخصص",
  },
  practice_field: {
    en: "Practice Field",
    ar: "مجال الممارسة",
  },
  doctor_address_governorate: {
    en: "Doctor Address Governorate",
    ar: "محافظة عنوان الطبيب",
  },
  doctor_address_district: {
    en: "Doctor Address District",
    ar: "قضاء عنوان الطبيب",
  },
  doctor_address_subdistrict: {
    en: "Doctor Address Subdistrict",
    ar: "ناحية عنوان الطبيب",
  },
  clinic_address_governorate: {
    en: "Clinic Address Governorate",
    ar: "محافظة عنوان العيادة",
  },
  clinic_address_district: {
    en: "Clinic Address District",
    ar: "قضاء عنوان العيادة",
  },
  clinic_address_subdistrict: {
    en: "Clinic Address Subdistrict",
    ar: "ناحية عنوان العيادة",
  },
  clinic_address_alley: {
    en: "Clinic Address Alley",
    ar: "زقاق عنوان العيادة",
  },
  clinic_address_nearest_landmark: {
    en: "Clinic Nearest Landmark",
    ar: "أقرب معلم للعيادة",
  },
  morning_workplace_ministry: {
    en: "Morning Workplace Ministry",
    ar: "وزارة مكان العمل الصباحي",
  },
  morning_workplace_general_directorate: {
    en: "Morning Workplace General Directorate",
    ar: "المديرية العامة لمكان العمل الصباحي",
  },
  morning_workplace_hospital: {
    en: "Morning Workplace Hospital",
    ar: "مستشفى مكان العمل الصباحي",
  },
  morning_workplace_university: {
    en: "Morning Workplace University",
    ar: "جامعة مكان العمل الصباحي",
  },
  morning_workplace_college: {
    en: "Morning Workplace College",
    ar: "كلية مكان العمل الصباحي",
  },
  clinic_working_hours_morning: {
    en: "Morning Working Hours",
    ar: "ساعات العمل الصباحية",
  },
  clinic_working_hours_evening: {
    en: "Evening Working Hours",
    ar: "ساعات العمل المسائية",
  },
  does_supervise_medical_center: {
    en: "Supervises Medical Center",
    ar: "يشرف على مركز طبي",
  },
  area_id: {
    en: "Area",
    ar: "المنطقة",
  },
  is_existing_clinic: {
    en: "Existing Clinic",
    ar: "عيادة موجودة",
  },
  established_date: {
    en: "Established Date",
    ar: "تاريخ التأسيس",
  },
  registration_details: {
    en: "Registration Details",
    ar: "تفاصيل التسجيل",
  },
  // Additional clinic form translations
  general_directorate: {
    en: "General Directorate",
    ar: "المديرية العامة",
  },
  morning_workplace: {
    en: "Morning Workplace",
    ar: "مكان العمل الصباحي",
  },
  clinic_working_hours: {
    en: "Clinic Working Hours",
    ar: "ساعات عمل العيادة",
  },
  morning_shift: {
    en: "Morning Shift",
    ar: "الفترة الصباحية",
  },
  evening_shift: {
    en: "Evening Shift",
    ar: "الفترة المسائية",
  },
  operational_details: {
    en: "Operational Details",
    ar: "التفاصيل التشغيلية",
  },
  doctor_address: {
    en: "Doctor Address",
    ar: "عنوان الطبيب",
  },

  // Request and Clinic Management Screen Translations
  error: {
    en: "Error",
    ar: "خطأ",
  },
  request_not_found: {
    en: "Request not found",
    ar: "الطلب غير موجود",
  },
  failed_to_load_request_details: {
    en: "Failed to load request details",
    ar: "فشل في تحميل تفاصيل الطلب",
  },
  back_to_requests: {
    en: "Back to Requests",
    ar: "العودة إلى الطلبات",
  },
  request_details: {
    en: "Request Details",
    ar: "تفاصيل الطلب",
  },
  clinic_registration: {
    en: "Clinic Registration",
    ar: "تسجيل العيادة",
  },
  clinic_onboarding: {
    en: "Clinic Onboarding",
    ar: "ضم العيادة",
  },
  status_pending: {
    en: "Pending",
    ar: "قيد المراجعة",
  },
  status_draft: {
    en: "Draft",
    ar: "مسودة",
  },
  status_approved: {
    en: "Approved",
    ar: "مقبول",
  },
  status_rejected: {
    en: "Rejected",
    ar: "مرفوض",
  },
  status_submitted: {
    en: "Submitted",
    ar: "تم التقديم",
  },
  status_new: {
    en: "New",
    ar: "جديد",
  },
  status_data_requested: {
    en: "Data Requested",
    ar: "البيانات مطلوبة",
  },
  status_terms_agreed: {
    en: "Terms Agreed",
    ar: "تم الموافقة على الشروط",
  },
  status_documents_uploaded: {
    en: "Documents Uploaded",
    ar: "تم رفع المستندات",
  },
  status_pending_inspection: {
    en: "Pending Inspection",
    ar: "بانتظار الكشف",
  },
  status_pending_payment: {
    en: "Pending Payment",
    ar: "بانتظار الدفع",
  },
  status_in_review: {
    en: "In Review",
    ar: "قيد المراجعة",
  },
  status_cancelled: {
    en: "Cancelled",
    ar: "ملغي",
  },
  status_completed: {
    en: "Completed",
    ar: "مكتمل",
  },
  status_changes_requested: {
    en: "Changes Requested",
    ar: "مطلوب تعديلات",
  },
  status_under_review: {
    en: "Under Review",
    ar: "قيد المراجعة",
  },
  status_payment_completed: {
    en: "Payment completed",
    ar: "تم الدفع",
  },
  status_inspection_scheduled: {
    en: "Inspection scheduled",
    ar: "تم جدولة الكشف",
  },
  status_inspection_completed: {
    en: "Inspection completed",
    ar: "اكتمل الكشف",
  },
  status_to_board: {
    en: "Sent to board",
    ar: "محال إلى المجلس",
  },
  status_reviewed: {
    en: "Reviewed",
    ar: "تمت المراجعة",
  },
  status_preliminary_approved: {
    en: "Preliminary approval",
    ar: "موافقة مبدئية",
  },
  request_preliminary_approved_message: {
    en: "Preliminarily approved — awaiting review and final approval",
    ar: "تمت الموافقة المبدئية وبانتظار المراجعة والموافقة النهائية",
  },
  request_pending_message: {
    en: "Your request is being reviewed. We'll notify you once it's processed.",
    ar: "طلبك قيد المراجعة. سنخبرك عند معالجته.",
  },
  request_draft_message: {
    en: "Your request is saved as a draft. You can continue completing it.",
    ar: "طلبك محفوظ كمسودة. يمكنك متابعة إكماله.",
  },
  corrections_required_message: {
    en: "Your request requires corrections. Please review and update the flagged fields.",
    ar: "طلبك يتطلب تعديلات. يرجى مراجعة وتحديث الحقول المحددة.",
  },
  corrections_required: {
    en: "Corrections Required",
    ar: "التعديلات المطلوبة",
  },
  reviewer_notes: {
    en: "Reviewer Notes",
    ar: "ملاحظات المراجع",
  },
  edit_and_resubmit: {
    en: "Edit & Resubmit",
    ar: "تعديل وإعادة الإرسال",
  },
  update_fields: {
    en: "Update Fields",
    ar: "تحديث الحقول",
  },
  failed_to_submit_corrections: {
    en: "Failed to submit corrections. Please try again.",
    ar: "فشل في إرسال التعديلات. يرجى المحاولة مرة أخرى.",
  },
  resubmit: {
    en: "Resubmit",
    ar: "إعادة الإرسال",
  },
  submitting: {
    en: "Submitting...",
    ar: "جاري الإرسال...",
  },
  select: {
    en: "Select",
    ar: "اختر",
  },
  request_information: {
    en: "Request Information",
    ar: "معلومات الطلب",
  },
  request_type: {
    en: "Request Type",
    ar: "نوع الطلب",
  },
  request_number: {
    en: "Request Number",
    ar: "رقم الطلب",
  },
  submitted_on: {
    en: "Submitted On",
    ar: "تاريخ التقديم",
  },
  last_updated: {
    en: "Last Updated",
    ar: "آخر تحديث",
  },
  clinic_name: {
    en: "Clinic Name",
    ar: "اسم العيادة",
  },
  work_hours: {
    en: "Work Hours",
    ar: "ساعات العمل",
  },
  location_information: {
    en: "Location Information",
    ar: "معلومات الموقع",
  },
  additional_notes: {
    en: "Additional Notes",
    ar: "ملاحظات إضافية",
  },
  file: {
    en: "file",
    ar: "ملف",
  },
  files: {
    en: "files",
    ar: "ملفات",
  },
  rejection_reason: {
    en: "Rejection Reason",
    ar: "سبب الرفض",
  },
  close: {
    en: "Close",
    ar: "إغلاق",
  },
  support: {
    en: "Support",
    ar: "الدعم",
  },
  support_message: {
    en: "Please tap the phone number to be directed to the support page on WhatsApp.",
    ar: "يرجى الضغط على رقم الهاتف لتحويلك لصفحة الدعم على الواتساب",
  },
  support_message_details: {
    en: "Please send us the details of your issue in a WhatsApp message, attaching a photo of your syndicate ID.",
    ar: "يرجى تزويدنا بتفاصيل مشكلتك برسالة على الواتساب مع ارفاق صورة الهوية النقابية",
  },
  download: {
    en: "Download",
    ar: "تحميل",
  },
  document_preview: {
    en: "Document preview",
    ar: "معاينة الوثيقة",
  },
  my_requests: {
    en: "My Requests",
    ar: "طلباتي",
  },
  request: {
    en: "request",
    ar: "طلب",
  },
  requests: {
    en: "requests",
    ar: "طلبات",
  },
  type: {
    en: "Type",
    ar: "النوع",
  },
  location: {
    en: "Location",
    ar: "الموقع",
  },
  submitted: {
    en: "Submitted",
    ar: "تم التقديم",
  },
  tap_to_continue: {
    en: "Tap to continue editing",
    ar: "اضغط للمتابعة",
  },
  tap_to_view_details: {
    en: "Tap to view details",
    ar: "اضغط لعرض التفاصيل",
  },
  clinic_management: {
    en: "Clinic Management",
    ar: "إدارة العيادة",
  },
  clinic_active: {
    en: "Clinic Active",
    ar: "العيادة نشطة",
  },
  clinic_status: {
    en: "Clinic Status",
    ar: "حالة العيادة",
  },
  clinic_status_draft: {
    en: "Draft",
    ar: "مسودة",
  },
  clinic_status_pending_registration: {
    en: "Pending Registration",
    ar: "بانتظار التسجيل",
  },
  clinic_status_registered: {
    en: "Registered",
    ar: "مسجلة",
  },
  clinic_status_active: {
    en: "Active",
    ar: "نشطة",
  },
  clinic_status_inactive: {
    en: "Inactive",
    ar: "غير نشطة",
  },
  clinic_status_suspended: {
    en: "Suspended",
    ar: "موقوفة",
  },
  clinic_status_closed: {
    en: "Closed",
    ar: "مغلقة",
  },
  license_details: {
    en: "License Details",
    ar: "تفاصيل الإجازة",
  },
  opened_date: {
    en: "Opening Date",
    ar: "تاريخ الافتتاح",
  },
  basic_information: {
    en: "Basic Information",
    ar: "المعلومات الأساسية",
  },
  clinic_type: {
    en: "Type",
    ar: "النوع",
  },
  license_number: {
    en: "License Number",
    ar: "رقم التسجيل",
  },
  phone: {
    en: "Phone",
    ar: "الهاتف",
  },
  building_number: {
    en: "Building",
    ar: "رقم المبنى",
  },
  floor_number: {
    en: "Floor",
    ar: "الطابق",
  },
  clinic_number: {
    en: "Clinic Number",
    ar: "رقم العيادة",
  },
  opening_hours: {
    en: "Opening Hours",
    ar: "ساعات الافتتاح",
  },
  services: {
    en: "Services",
    ar: "الخدمات",
  },
  registered_on: {
    en: "Registered On",
    ar: "تاريخ التسجيل",
  },
  clinic_actions: {
    en: "Clinic Actions",
    ar: "إجراءات العيادة",
  },
  renew_license: {
    en: "Renew License",
    ar: "تجديد الرخصة",
  },
  relocate_clinic: {
    en: "Relocate Clinic",
    ar: "نقل العيادة",
  },
  // Features that are not offered yet: say so plainly, without promising a date.
  coming_soon: {
    en: "Currently unavailable",
    ar: "غير متاح حالياً",
  },
  register_new_clinic: {
    en: "Register New Clinic",
    ar: "تسجيل عيادة جديدة",
  },
  register_another_clinic: {
    en: "Register Another Clinic",
    ar: "تسجيل عيادة أخرى",
  },
  welcome_doctor: {
    en: "Welcome, Doctor",
    ar: "مرحباً، دكتور",
  },
  pending_request: {
    en: "Pending Request",
    ar: "طلب معلق",
  },
  clinic_registration_request: {
    en: "Clinic Registration Request",
    ar: "طلب تسجيل عيادة",
  },
  clinic_onboarding_request: {
    en: "Clinic Onboarding Request",
    ar: "طلب ضم عيادة",
  },
  cancel_request: {
    en: "Cancel Request",
    ar: "إلغاء الطلب",
  },
  no_clinics_yet: {
    en: "No Clinics Yet",
    ar: "لا توجد عيادات بعد",
  },
  get_started_message: {
    en: "Get started by registering your clinic",
    ar: "ابدأ بتسجيل عيادتك",
  },
  open_new_clinic: {
    en: "Open New Clinic",
    ar: "فتح عيادة جديدة",
  },
  clinic_choice_warning: {
    en: "If you already have an open clinic, you must close it first before opening a new one.",
    ar: "إذا كان لديك عيادة مفتوحة يجب عليك غلقها أولاً وبعد ذلك فتح عيادة جديدة",
  },
  onboard_existing_clinic: {
    en: "Register a Previously Registered Clinic (You Hold a Clinic Opening License)",
    ar: "تسجيل عيادة مسجلة مسبقا (تمتلك اجازة فتح عيادة)",
  },
  onboard_confirm_title: {
    en: "Do you already hold a clinic opening license?",
    ar: "هل تمتلك إجازة فتح عيادة مسبقاً؟",
  },
  onboard_confirm_message: {
    en: "This option is only for a clinic that is already registered and for which you already hold a clinic opening license. If you want to open a brand new clinic, go back and choose \"Open New Clinic\" instead.",
    ar: "هذا الخيار مخصص فقط للعيادات المسجلة مسبقاً والتي تمتلك إجازة فتح عيادة. أما إذا كنت ترغب بفتح عيادة جديدة، فيجب عليك الرجوع واختيار «فتح عيادة جديدة».",
  },
  onboard_confirm_proceed: {
    en: "Yes, I have a license — Continue",
    ar: "نعم، أمتلك الإجازة — متابعة",
  },
  confirm_cancel_request: {
    en: "Cancel Request?",
    ar: "إلغاء الطلب؟",
  },
  cancel_request_message: {
    en: "Are you sure you want to cancel this request? This action cannot be undone.",
    ar: "هل أنت متأكد من رغبتك في إلغاء هذا الطلب؟ لا يمكن التراجع عن هذا الإجراء.",
  },
  no_keep_it: {
    en: "No, Keep It",
    ar: "لا، احتفظ به",
  },
  yes_cancel_it: {
    en: "Yes, Cancel It",
    ar: "نعم، قم بإلغائه",
  },
  // Classic ↔ new design switch
  switch_to_new_design: {
    en: "New design",
    ar: "التصميم الجديد",
  },
  switch_to_classic_design: {
    en: "Classic design",
    ar: "التصميم الكلاسيكي",
  },
  design_switch_unsaved_title: {
    en: "Discard unsaved changes?",
    ar: "تجاهل التغييرات غير المحفوظة؟",
  },
  design_switch_unsaved_message: {
    en: "You have changes on this page that are not saved yet. Switching the design reloads the page and they will be lost.",
    ar: "لديك تغييرات في هذه الصفحة لم تُحفظ بعد. تبديل التصميم يعيد تحميل الصفحة وستُفقد هذه التغييرات.",
  },
  design_switch_discard_and_switch: {
    en: "Discard and switch",
    ar: "تجاهل والتبديل",
  },
  design_switch_keep_editing: {
    en: "Keep editing",
    ar: "متابعة التعديل",
  },
  confirm_logout: {
    en: "Confirm Logout",
    ar: "تأكيد تسجيل الخروج",
  },
  logout_confirmation_message: {
    en: "Are you sure you want to logout? You will need to login again to access your account.",
    ar: "هل أنت متأكد من رغبتك في تسجيل الخروج؟ ستحتاج إلى تسجيل الدخول مرة أخرى للوصول إلى حسابك.",
  },
  cancel: {
    en: "Cancel",
    ar: "إلغاء",
  },
  camera_access_denied: {
    en: "Camera access denied. Please allow camera permissions.",
    ar: "تم رفض الوصول إلى الكاميرا. يرجى السماح بأذونات الكاميرا.",
  },
  try_again: {
    en: "Try Again",
    ar: "حاول مرة أخرى",
  },
  capture: {
    en: "Capture",
    ar: "التقاط",
  },
  align_document_in_frame: {
    en: "Align the document within the frame",
    ar: "قم بمحاذاة المستند داخل الإطار",
  },
  adjust_crop: {
    en: "Adjust Crop",
    ar: "ضبط الاقتصاص",
  },
  crop_helper_text: {
    en: "Drag to position, pinch or use the slider to zoom",
    ar: "اسحب لتغيير الموضع، أو استخدم شريط التمرير للتكبير",
  },
  use_photo: {
    en: "Use Photo",
    ar: "استخدام الصورة",
  },
  retake: {
    en: "Retake",
    ar: "إعادة التقاط",
  },
  rotate: {
    en: "Rotate",
    ar: "تدوير",
  },
  zoom: {
    en: "Zoom",
    ar: "تكبير",
  },
  please_upload_at_least_one_room_photo: {
    en: "Please upload at least one clinic room photo",
    ar: "يرجى تحميل صورة واحدة على الأقل لغرفة العيادة",
  },
  delete_account: {
    en: "Delete Account",
    ar: "حذف الحساب",
  },
  confirm_delete_account: {
    en: "Delete Account?",
    ar: "حذف الحساب؟",
  },
  delete_account_warning: {
    en: "This action cannot be undone. All your data including profile information, requests, and documents will be permanently deleted.",
    ar: "لا يمكن التراجع عن هذا الإجراء. سيتم حذف جميع بياناتك بما في ذلك معلومات الملف الشخصي والطلبات والمستندات بشكل دائم.",
  },
  delete_account_consequences: {
    en: "⚠️ This will permanently delete your account and all associated data",
    ar: "⚠️ سيؤدي هذا إلى حذف حسابك وجميع البيانات المرتبطة به بشكل دائم",
  },
  yes_delete_account: {
    en: "Yes, Delete",
    ar: "نعم، احذف",
  },
  deleting: {
    en: "Deleting...",
    ar: "جاري الحذف...",
  },

  // Missing Field Translations
  medical_license_registration_number: {
    en: "Medical License Registration Number",
    ar: "رقم تسجيل التسجيل الطبية",
  },
  graduated_from_university: {
    en: "Graduated From University",
    ar: "تخرج من جامعة",
  },
  other_university: {
    en: "Other (outside Iraq)",
    ar: "أخرى (خارج العراق)",
  },
  university_name: {
    en: "University Name",
    ar: "اسم الجامعة",
  },
  enter_university_name: {
    en: "Enter your university name",
    ar: "أدخل اسم جامعتك",
  },
  equivalency_certificate_hint: {
    en: "You will be asked to upload an Iraqi certificate equivalency form in the documents step.",
    ar: "سيُطلب منك رفع صورة معادلة الشهادة في العراق في خطوة المستندات.",
  },
  organization: {
    en: "Organization",
    ar: "الدائرة",
  },
  work_college: {
    en: "Work College",
    ar: "كلية العمل",
  },
  work_university: {
    en: "Work University",
    ar: "جامعة العمل",
  },
  search_select_university: {
    en: "Search and select university",
    ar: "ابحث واختر الجامعة",
  },
  search_select_specialization: {
    en: "Search and select specialization",
    ar: "ابحث واختر التخصص",
  },
  search_select_practitioner: {
    en: "Search and select practitioner",
    ar: "ابحث واختر الممارس",
  },
  search_select_organization: {
    en: "Search and select organization",
    ar: "ابحث واختر الدائرة",
  },
  search_select_hospital: {
    en: "Search and select hospital",
    ar: "ابحث واختر المستشفى",
  },
  search_select_work_university: {
    en: "Search and select work university",
    ar: "ابحث واختر جامعة العمل",
  },
  search_select_work_college: {
    en: "Search and select work college",
    ar: "ابحث واختر كلية العمل",
  },
  doctor_info_subtitle: {
    en: "Please provide your personal and professional information",
    ar: "يرجى تقديم معلوماتك الشخصية والمهنية",
  },

  // Document Upload Translations
  syndicate_id_front: {
    en: "Syndicate ID (Front)",
    ar: "هوية النقابة (الأمامية)",
  },
  syndicate_id_back: {
    en: "Syndicate ID (Back)",
    ar: "هوية النقابة (الخلفية)",
  },
  specialization_certificate: {
    en: "The syndicate order for registering the specialty title",
    ar: "الأمر النقابي بتسجيل لقب الإختصاص",
  },
  certificate_equivalency: {
    en: "Certificate equivalency form (Iraq)",
    ar: "صورة معادلة الشهادة في العراق",
  },
  proof_of_practice: {
    en: "Confirmation from last workplace proving the doctor's transition to practitioner, or the syndicate ID",
    ar: "تأييد من آخر محل عمل يثبت تحول الطبيب الى ممارس أو الهوية النقابية",
  },
  // Ministry of Health doctors can obtain the workplace confirmation letter, so the
  // syndicate ID is not offered to them as a substitute.
  proof_of_practice_health_ministry: {
    en: "Confirmation from last workplace proving the doctor's transition to practitioner",
    ar: "تأييد من آخر محل عمل يثبت تحول الطبيب الى ممارس",
  },
  doctor_type: {
    en: "Doctor Type",
    ar: "نوع الطبيب",
  },
  specialist: {
    en: "Specialist",
    ar: "متخصص",
  },
  practitioner: {
    en: "Practitioner",
    ar: "ممارس عام",
  },
  practition: {
    en: "Practition",
    ar: "الممارسة",
  },
  // NOTE: the "higher_diploma" label itself already exists further down as a
  // medical-degree value with identical wording — the doctor-type radio reuses it.
  higher_diploma_order: {
    en: "Administrative order for the higher diploma",
    ar: "امر اداري الخاص بالدبلوم العالي",
  },
  please_upload_higher_diploma_order: {
    en: "Please upload the administrative order for the higher diploma",
    ar: "يرجى رفع الامر الاداري الخاص بالدبلوم العالي",
  },
  click_to_upload: {
    en: "Click to upload",
    ar: "انقر للرفع",
  },
  take_photo: {
    en: "Take Photo",
    ar: "التقط صورة",
  },
  flip_camera: {
    en: "Flip camera",
    ar: "تبديل الكاميرا",
  },
  choose_from_files: {
    en: "Choose from Files",
    ar: "اختر من الملفات",
  },
  submit_documents: {
    en: "Submit Documents",
    ar: "إرسال الوثائق",
  },
  upload_documents: {
    en: "Upload Documents",
    ar: "رفع الوثائق",
  },
  applicant_notes: {
    en: "Applicant Notes",
    ar: "ملاحظات مقدم الطلب",
  },

  // CareerProgression Enum Translations
  intern: {
    en: "Intern",
    ar: "متدرب",
  },
  resident: {
    en: "Resident",
    ar: "مقيم",
  },
  senior_specialist: {
    en: "Senior Specialist",
    ar: "أخصائي أول",
  },
  consultant: {
    en: "Consultant",
    ar: "استشاري",
  },
  senior_consultant: {
    en: "Senior Consultant",
    ar: "استشاري أول",
  },
  chief_consultant: {
    en: "Chief Consultant",
    ar: "استشاري رئيسي",
  },
  department_head: {
    en: "Department Head",
    ar: "رئيس قسم",
  },
  medical_director: {
    en: "Medical Director",
    ar: "مدير طبي",
  },
  professor: {
    en: "Professor",
    ar: "أستاذ",
  },
  associate_professor: {
    en: "Associate Professor",
    ar: "أستاذ مشارك",
  },
  assistant_professor: {
    en: "Assistant Professor",
    ar: "أستاذ مساعد",
  },

  // MedicalDegree Enum Translations
  mbbs: {
    en: "MBBS (Bachelor of Medicine, Bachelor of Surgery)",
    ar: "بكالوريوس الطب والجراحة العامة",
  },
  masters: {
    en: "Master's Degree",
    ar: "ماجستير",
  },
  phd: {
    en: "PhD (Doctor of Philosophy)",
    ar: "دكتوراة",
  },
  diploma: {
    en: "Diploma",
    ar: "دبلوم",
  },
  higher_diploma: {
    en: "Higher Diploma",
    ar: "دبلوم عالي",
  },
  fellowship: {
    en: "Fellowship",
    ar: "زمالة",
  },
  arab_board_fellow: {
    en: "Fellow of the Arab Board of Health Specializations",
    ar: "زميل المجلس العربي للاختصاصات الصحية",
  },
  iraqi_board_fellow: {
    en: "Fellow of the Iraqi Board of Medical Specializations",
    ar: "زميل المجلس العراقي للاختصاصات الطبية",
  },
  iraqi_board: {
    en: "Iraqi Board",
    ar: "بورد عراقي",
  },
  arab_board: {
    en: "Arab Board",
    ar: "بورد عربي",
  },
  egyptian_board: {
    en: "Egyptian Board",
    ar: "بورد مصري",
  },
  jordanian_board: {
    en: "Jordanian Board",
    ar: "بورد اردني",
  },
  european_board: {
    en: "European Board",
    ar: "بورد اوربي",
  },
  royal_college_edinburgh_fellowship: {
    en: "Fellowship of the Royal College of Edinburgh",
    ar: "زمالة الكلية الملكية البريطانية - إدنبرة",
  },
  // Retired — no longer selectable. Kept so doctors who registered under the old
  // combined "Arab or Iraqi board" option still see a label on their record.
  board_certification: {
    en: "Board Certification",
    ar: "شهادة المجلس العربي او العراقي",
  },

  // Language Enum Translations
  english: {
    en: "English",
    ar: "الإنجليزية",
  },
  arabic: {
    en: "Arabic",
    ar: "العربية",
  },
  kurdish: {
    en: "Kurdish",
    ar: "الكردية",
  },

  // Clinic Attachment Upload Translations
  clinic_signboard_outer: {
    en: "Clinic Signboard (Outer)",
    ar: "لافتة العيادة (الخارجية)",
  },
  clinic_signboard_inner: {
    en: "Clinic Signboard (Inner)",
    ar: "لافتة العيادة (الداخلية)",
  },
  clinic_rooms: {
    en: "Clinic Rooms",
    ar: "غرف العيادة",
  },
  other_attachments: {
    en: "Other Attachments",
    ar: "مرفقات أخرى",
  },
  other_attachments_description: {
    en: "Upload any additional supporting documents (optional, max 2)",
    ar: "قم بتحميل أي مستندات داعمة إضافية (اختياري، بحد أقصى 2)",
  },
  attachment: {
    en: "Attachment",
    ar: "مرفق",
  },
  clinic_photos: {
    en: "Clinic Photos",
    ar: "صور العيادة",
  },
  // Document type base keys (for RequestDetailScreen)
  syndicate_order: {
    en: "Syndicate Order",
    ar: "الأمر النقابي بفتح العيادة",
  },
  prescription_image: {
    en: "Prescription Sample",
    ar: "نموذج الوصفة طبية",
  },
  opening_certificate: {
    en: "Opening License",
    ar: "إجازة فتح العيادة",
  },
  onboarding_photos: {
    en: "Clinic Photos & Documents",
    ar: "صور ومستندات العيادة",
  },
  upload_onboarding_documents: {
    en: "Upload clinic photos and required documents",
    ar: "قم بتحميل صور العيادة والمستندات المطلوبة",
  },
  syndicate_order_title: {
    en: "Syndicate Order",
    ar: "الأمر النقابي بفتح العيادة",
  },
  syndicate_order_number: {
    en: "Syndicate Order Number",
    ar: "رقم الأمر النقابي بفتح العيادة",
  },
  enter_syndicate_order_number: {
    en: "Enter syndicate order number",
    ar: "أدخل رقم الأمر النقابي بفتح العيادة",
  },
  syndicate_order_description: {
    en: "Upload the syndicate order document",
    ar: "قم بتحميل صورة الأمر النقابي بفتح العيادة",
  },
  opening_certificate_title: {
    en: "Opening License",
    ar: "إجازة فتح العيادة",
  },
  opening_certificate_number: {
    en: "Opening License Number",
    ar: "رقم إجازة فتح العيادة",
  },
  enter_opening_certificate_number: {
    en: "Enter opening license number",
    ar: "أدخل رقم إجازة فتح العيادة",
  },
  opening_certificate_description: {
    en: "Upload the clinic opening license",
    ar: "قم بتحميل صورة إجازة فتح العيادة",
  },
  please_upload_syndicate_order: {
    en: "Please upload the syndicate order",
    ar: "يرجى تحميل الأمر النقابي بفتح العيادة",
  },
  please_upload_opening_certificate: {
    en: "Please upload the opening license",
    ar: "يرجى تحميل إجازة فتح العيادة",
  },
  onboarding_photos_note: {
    en: "Please upload clear photos of your clinic signboards, rooms, and required documents for onboarding.",
    ar: "يرجى تحميل صور واضحة للافتات العيادة والغرف والمستندات المطلوبة للتسجيل.",
  },
  loading_data: {
    en: "Loading Data",
    ar: "جاري تحميل البيانات",
  },
  no_photo_uploaded: {
    en: "No photo uploaded",
    ar: "لم يتم رفع صورة",
  },

  // Error Messages
  failed_to_update_doctor_information: {
    en: "Failed to update doctor information",
    ar: "فشل تحديث معلومات الطبيب",
  },
  fix_errors_to_continue: {
    en: "Fix {count} error{plural} to continue",
    ar: "أصلح {count} خطأ للمتابعة",
  },
  please_fix_following_errors: {
    en: "Please fix the following errors:",
    ar: "يرجى إصلاح الأخطاء التالية:",
  },
  file_size_must_be_less_than_10mb: {
    en: "File size must be less than 10MB",
    ar: "يجب أن يكون حجم الملف أقل من 10 ميجابايت",
  },
  file_must_be_image_or_pdf: {
    en: "This file type is not accepted. Choose a photo (JPG, PNG, WEBP, HEIC) or a PDF.",
    ar: "نوع الملف غير مقبول. اختر صورة (JPG أو PNG أو WEBP أو HEIC) أو ملف PDF.",
  },
  file_is_empty: {
    en: "The file is empty. Choose another file.",
    ar: "الملف فارغ. اختر ملفاً آخر.",
  },
  upload_failed: {
    en: "Upload failed",
    ar: "فشل الرفع",
  },
  please_upload_syndicate_id_front: {
    en: "Please upload Syndicate ID (Front)",
    ar: "يرجى رفع هوية النقابة (الأمامية)",
  },
  please_upload_syndicate_id_back: {
    en: "Please upload Syndicate ID (Back)",
    ar: "يرجى رفع هوية النقابة (الخلفية)",
  },
  please_upload_specialization_certificate: {
    en: "Please upload the syndicate order for registering the specialty title",
    ar: "يرجى رفع الأمر النقابي بتسجيل لقب الإختصاص",
  },
  please_upload_certificate_equivalency: {
    en: "Please upload the certificate equivalency form (Iraq)",
    ar: "يرجى رفع صورة معادلة الشهادة في العراق",
  },
  please_upload_proof_of_practice: {
    en: "Please upload Confirmation from last workplace proving the doctor's transition to practitioner, or the syndicate ID",
    ar: "يرجى رفع تأييد من آخر محل عمل يثبت تحول الطبيب الى ممارس أو الهوية النقابية",
  },
  please_upload_proof_of_practice_health_ministry: {
    en: "Please upload Confirmation from last workplace proving the doctor's transition to practitioner",
    ar: "يرجى رفع تأييد من آخر محل عمل يثبت تحول الطبيب الى ممارس",
  },
  some_documents_failed_to_upload: {
    en: "Some documents failed to upload. Please try again.",
    ar: "فشل رفع بعض الوثائق. يرجى المحاولة مرة أخرى.",
  },
  failed_to_upload_documents: {
    en: "Failed to upload documents",
    ar: "فشل رفع الوثائق",
  },
  please_select_image_file: {
    en: "This file type is not accepted. Choose a photo (JPG, PNG, WEBP, HEIC).",
    ar: "نوع الملف غير مقبول. اختر صورة (JPG أو PNG أو WEBP أو HEIC).",
  },
  registration_request_not_found: {
    en: "Registration request not found. Please try again.",
    ar: "لم يتم العثور على طلب التسجيل. يرجى المحاولة مرة أخرى.",
  },
  failed_to_upload_document_type: {
    en: "Failed to upload {type}",
    ar: "فشل رفع {type}",
  },
  please_upload_clinic_signboard_outer: {
    en: "Please upload clinic signboard (outer) photo",
    ar: "يرجى رفع صورة لافتة العيادة (الخارجية)",
  },
  please_upload_clinic_signboard_inner: {
    en: "Please upload clinic signboard (inner) photo",
    ar: "يرجى رفع صورة لافتة العيادة (الداخلية)",
  },
  failed_to_load_data: {
    en: "Failed to load data",
    ar: "فشل تحميل البيانات",
  },
  failed_to_cancel_request: {
    en: "Failed to cancel request",
    ar: "فشل إلغاء الطلب",
  },

  // File Upload Related
  max_10mb_image_or_pdf: {
    en: "Photo (JPG, PNG, WEBP, HEIC) or PDF · up to 10 MB",
    ar: "صورة (JPG أو PNG أو WEBP أو HEIC) أو ملف PDF · حتى 10 ميجابايت",
  },
  pdf_file: {
    en: "PDF File",
    ar: "ملف PDF",
  },
  png_jpg_up_to_10mb: {
    en: "Photo (JPG, PNG, WEBP, HEIC) · up to 10 MB",
    ar: "صورة (JPG أو PNG أو WEBP أو HEIC) · حتى 10 ميجابايت",
  },

  // Button Text
  change_photo: {
    en: "Change Photo",
    ar: "تغيير الصورة",
  },
  upload_and_continue: {
    en: "Upload & Continue",
    ar: "رفع ومتابعة",
  },
  add_room_photo: {
    en: "Add Room Photo",
    ar: "إضافة صورة غرفة",
  },

  // UI Labels and Descriptions
  loading_existing_documents: {
    en: "Loading existing documents...",
    ar: "جاري تحميل الوثائق الموجودة...",
  },
  upload_photos_clinic_signboards_rooms: {
    en: "Upload photos of your clinic signboards and rooms",
    ar: "رفع صور لافتات العيادة والغرف",
  },
  registration_complete_banner_title: {
    en: "✓ Registration Complete:",
    ar: "✓ التسجيل مكتمل:",
  },
  registration_complete_banner_message: {
    en: "Your clinic photos have been submitted successfully. You can view them below but cannot make changes at this time.",
    ar: "تم إرسال صور عيادتك بنجاح. يمكنك عرضها أدناه ولكن لا يمكنك إجراء تغييرات في الوقت الحالي.",
  },
  clinic_signboard_outer_title: {
    en: "Clinic Signboard (Outer)",
    ar: "لافتة العيادة (الخارجية)",
  },
  clinic_signboard_outer_description: {
    en: "Photo of the clinic signboard from outside",
    ar: "صورة لافتة العيادة من الخارج",
  },
  clinic_signboard_inner_title: {
    en: "Clinic Signboard (Inner)",
    ar: "لافتة العيادة (الداخلية)",
  },
  clinic_signboard_inner_description: {
    en: "Photo of the clinic signboard from inside",
    ar: "صورة لافتة العيادة من الداخل",
  },
  prescription_image_title: {
    en: "Prescription Sample",
    ar: "نموذج الوصفة الطبية",
  },
  prescription_image_description: {
    en: "Photo of a prescription sample from the clinic",
    ar: "صورة نموذج وصفة طبية من العيادة",
  },
  please_upload_prescription_image: {
    en: "Please upload a prescription sample",
    ar: "يرجى تحميل نموذج وصفة طبية",
  },
  no_clinic_in_residence_title: {
    en: "Confirmation of No Clinic in Residence Governorate",
    ar: "تأييد بعدم وجود عيادة في محافظة الإقامة",
  },
  no_clinic_in_residence_description: {
    en: "Upload confirmation from the syndicate branch in {governorate} stating you do not have a clinic there",
    ar: "قم بتحميل تأييد من فرع النقابة في {governorate} يفيد بعدم وجود عيادة لديك هناك",
  },
  please_upload_no_clinic_in_residence: {
    en: "Please upload confirmation from the syndicate branch in {governorate}",
    ar: "يرجى تحميل تأييد من فرع النقابة في {governorate}",
  },
  clinic_rooms_optional: {
    en: "Clinic Rooms (Optional)",
    ar: "غرف العيادة (اختياري)",
  },
  no_room_photos_added: {
    en: "No room photos added yet. Click \"Add Room Photo\" to upload.",
    ar: "لم يتم إضافة صور غرف بعد. انقر على \"إضافة صورة غرفة\" للرفع.",
  },
  room_photo_number: {
    en: "Room Photo {number}",
    ar: "صورة غرفة {number}",
  },
  photo_of_clinic_room: {
    en: "Photo of clinic room {number}",
    ar: "صورة غرفة العيادة {number}",
  },
  note_label: {
    en: "Note:",
    ar: "ملاحظة:",
  },
  clinic_photos_note_message: {
    en: "All photos should be clear and show the clinic signboards prominently. At least one room photo is required; add a photo of each room.",
    ar: "يجب أن تكون جميع الصور واضحة وتظهر لافتات العيادة بشكل بارز. صورة غرفة واحدة على الأقل مطلوبة، وأضف صورة لكل غرفة.",
  },

  // Placeholder Text
  enter_clinic_name: {
    en: "Enter clinic name",
    ar: "أدخل اسم العيادة",
  },
  enter_city_name: {
    en: "Enter city name",
    ar: "أدخل اسم المدينة",
  },
  select_city: {
    en: "Select City",
    ar: "اختر المدينة",
  },
  neighborhood_placeholder: {
    en: "123",
    ar: "123",
  },
  street_placeholder: {
    en: "45",
    ar: "45",
  },
  house_number_placeholder: {
    en: "12",
    ar: "12",
  },
  enter_nearest_landmark: {
    en: "Enter nearest landmark",
    ar: "أدخل أقرب نقطة دالة",
  },
  search_for_area: {
    en: "Search for area...",
    ar: "ابحث عن المنطقة...",
  },
  select_governorate_first: {
    en: "Select governorate first...",
    ar: "اختر المحافظة أولاً...",
  },
  add_additional_notes: {
    en: "Add any additional notes...",
    ar: "أضف أي ملاحظات إضافية...",
  },

  // Validation Error Messages
  field_is_required: {
    en: "{field} is required",
    ar: "{field} مطلوب",
  },
  invalid_governorate: {
    en: "Invalid governorate",
    ar: "محافظة غير صالحة",
  },
  service_not_available: {
    en: "Service Not Available",
    ar: "الخدمة غير متوفرة",
  },
  governorate_not_available_message: {
    en: "The service is currently not available in your governorate. Please visit the governorate branch for registration.",
    ar: "الخدمة غير متوفرة حالياً في محافظتك. يرجى زيارة فرع المحافظة للتسجيل.",
  },
  understood: {
    en: "Understood",
    ar: "مفهوم",
  },
  clinic_name_length_error: {
    en: "Clinic name must be between 2 and 255 characters",
    ar: "يجب أن يكون اسم العيادة بين 2 و 255 حرفاً",
  },
  city_length_error: {
    en: "City must be between 2 and 255 characters",
    ar: "يجب أن يكون اسم المدينة بين 2 و 255 حرفاً",
  },
  nearest_landmark_length_error: {
    en: "Nearest landmark must be between 2 and 255 characters",
    ar: "يجب أن تكون أقرب نقطة دالة بين 2 و 255 حرفاً",
  },
  neighborhood_must_be_number: {
    en: "Neighborhood must be a number",
    ar: "يجب أن تكون المحلة رقماً",
  },
  street_must_be_number: {
    en: "Street must be a number",
    ar: "يجب أن يكون الشارع رقماً",
  },
  house_number_must_be_number: {
    en: "House number must be a number",
    ar: "يجب أن يكون رقم الدار رقماً",
  },
  invalid_uuid_format: {
    en: "Invalid UUID format",
    ar: "صيغة معرف فريد غير صالحة",
  },
  select_valid_area: {
    en: "Please select a valid area from the list",
    ar: "يرجى اختيار منطقة صالحة من القائمة",
  },
  invalid_latitude: {
    en: "Invalid latitude",
    ar: "خط عرض غير صالح",
  },
  invalid_longitude: {
    en: "Invalid longitude",
    ar: "خط طول غير صالح",
  },
  failed_load_areas_refresh: {
    en: "Failed to load areas. Please try refreshing the page.",
    ar: "فشل تحميل المناطق. يرجى تحديث الصفحة.",
  },
  morning: {
    en: "Morning",
    ar: "صباحي",
  },
  evening: {
    en: "Evening",
    ar: "مسائي",
  },
  skip: {
    en: "Skip",
    ar: "تخطي",
  },
  doctor_name: {
    en: "Doctor Name",
    ar: "اسم الطبيب",
  },
  scan_to_view: {
    en: "Scan to View",
    ar: "امسح للعرض",
  },
  printed_on: {
    en: "Printed on",
    ar: "طبع في",
  },
  print_request: {
    en: "Print Request",
    ar: "طباعة الطلب",
  },
  public_view: {
    en: "Public View",
    ar: "عرض عام",
  },
  public_view_notice: {
    en: "This is a public view of the registration request. Some sensitive information may be hidden.",
    ar: "هذا عرض عام لطلب التسجيل. قد تكون بعض المعلومات الحساسة مخفية.",
  },
  public_request_not_available: {
    en: "This request is not available for public viewing.",
    ar: "هذا الطلب غير متاح للعرض العام.",
  },
  public_view_footer: {
    en: "This information is provided for verification purposes only and should not be used for any unauthorized purpose.",
    ar: "هذه المعلومات مقدمة لأغراض التحقق فقط ولا ينبغي استخدامها لأي غرض غير مصرح به.",
  },
  print_qr_door_sign: {
    en: "Print the QR code below on the clinic door and it will be part of the field inspection process",
    ar: "قم بطباعة الكيو ار ادناه على باب العيادة وسيكون جزء من عملية الكشف الميداني",
  },
  qr_door_sign: {
    en: "Clinic QR Door Sign",
    ar: "لوحة الكيو ار للعيادة",
  },
  scan_to_verify: {
    en: "Scan to verify this clinic",
    ar: "امسح الرمز للتحقق من هذه العيادة",
  },
  clinic_verification: {
    en: "Clinic Verification",
    ar: "التحقق من العيادة",
  },
  clinic_approved_verified: {
    en: "Approved and registered clinic",
    ar: "عيادة معتمدة ومسجلة",
  },
  clinic_approved_verified_message: {
    en: "This clinic is officially approved and registered with the Iraqi Medical Association.",
    ar: "هذه العيادة معتمدة ومسجلة رسمياً لدى نقابة الأطباء العراقية.",
  },
  clinic_not_verified: {
    en: "Clinic not verified",
    ar: "العيادة غير موثقة",
  },
  clinic_not_verified_message: {
    en: "No approved clinic matches this code. Please contact the Iraqi Medical Association if you believe this is an error.",
    ar: "لا توجد عيادة معتمدة مطابقة لهذا الرمز. يرجى التواصل مع نقابة الأطباء العراقية إذا كنت تعتقد أن هناك خطأ.",
  },
  print_qr_code: {
    en: "Print QR Code",
    ar: "طباعة رمز QR",
  },
  iraqi_medical_association: {
    en: "Iraqi Medical Association",
    ar: "نقابة اطباء العراق",
  },
  clinic_documentation_platform: {
    en: "Clinic Documentation Platform",
    ar: "منصة توثيق العيادات",
  },
  // Error Messages
  failed_to_get_phone: {
    en: "Failed to get phone number",
    ar: "فشل في الحصول على رقم الهاتف",
  },
  failed_to_send_otp: {
    en: "Failed to send OTP",
    ar: "فشل في إرسال رمز التحقق",
  },
  failed_to_resend_otp: {
    en: "Failed to resend OTP",
    ar: "فشل في إعادة إرسال رمز التحقق",
  },
  failed_to_submit_clinic_info: {
    en: "Failed to submit clinic information",
    ar: "فشل في إرسال معلومات العيادة",
  },
  failed_to_load_clinic: {
    en: "Failed to load clinic",
    ar: "فشل في تحميل العيادة",
  },
  failed_to_fetch_doctor_info: {
    en: "Failed to fetch doctor information",
    ar: "فشل في جلب معلومات الطبيب",
  },
  failed_to_initiate_payment: {
    en: "Failed to initiate payment",
    ar: "فشل في بدء الدفع",
  },
  failed_to_cancel_payment: {
    en: "Failed to cancel payment",
    ar: "فشل في إلغاء الدفع",
  },
  failed_to_skip_payment: {
    en: "Failed to skip payment",
    ar: "فشل في تخطي الدفع",
  },
  payment_failed_try_again: {
    en: "Payment failed. Please try again.",
    ar: "فشل الدفع. يرجى المحاولة مرة أخرى.",
  },
  failed_to_register_agreement: {
    en: "Failed to register agreement to terms",
    ar: "فشل في تسجيل الموافقة على الشروط",
  },
  failed_to_proceed_payment: {
    en: "Failed to proceed to payment",
    ar: "فشل في المتابعة للدفع",
  },
  please_agree_to_pledges: {
    en: "Please agree to all required pledges to continue",
    ar: "يرجى الموافقة على جميع التعهدات المطلوبة للمتابعة",
  },
  please_fix_errors: {
    en: "Please fix the errors above",
    ar: "يرجى إصلاح الأخطاء أعلاه",
  },
  please_upload_room_photo: {
    en: "Please upload at least one clinic room photo",
    ar: "يرجى تحميل صورة واحدة على الأقل لغرفة العيادة",
  },
  failed_to_fetch_address: {
    en: "Failed to fetch address",
    ar: "فشل في جلب العنوان",
  },
  // Camera and Location (additional)
  align_document_frame: {
    en: "Align the document within the frame",
    ar: "قم بمحاذاة المستند داخل الإطار",
  },
  error_getting_location: {
    en: "Error getting your location",
    ar: "خطأ في الحصول على موقعك",
  },
  location_access_denied: {
    en: "Location access was denied. Please enable location permissions.",
    ar: "تم رفض الوصول للموقع. يرجى تفعيل أذونات الموقع.",
  },
  click_map_instruction: {
    en: "Click on the map to select a location, or use the GPS button to get your current location.",
    ar: "انقر على الخريطة لتحديد موقع، أو استخدم زر GPS للحصول على موقعك الحالي.",
  },
  // Document Upload (additional)
  uploaded_document: {
    en: "Uploaded Document",
    ar: "مستند مرفوع",
  },
  remove_room_photo: {
    en: "Remove room photo",
    ar: "إزالة صورة الغرفة",
  },
  // General
  not_available: {
    en: "Not Available",
    ar: "غير متاح",
  },
  an_error_occurred: {
    en: "An error occurred",
    ar: "حدث خطأ",
  },
  // =====================================
  // CLINIC CLOSURE TRANSLATIONS
  // =====================================
  close_existing_clinic: {
    en: "Close Existing Clinic",
    ar: "إغلاق عيادة قائمة",
  },
  close_existing_clinic_subtitle: {
    en: "For clinics not registered in the system",
    ar: "للعيادات غير المسجلة في النظام",
  },
  closure_warning_title: {
    en: "Important Notice",
    ar: "تنبيه مهم",
  },
  closure_warning_message: {
    en: "You are about to request closure of your registered clinic. This action will create a closure request that needs to be processed.",
    ar: "أنت على وشك طلب إغلاق عيادتك المسجلة. سيؤدي هذا الإجراء إلى إنشاء طلب إغلاق يحتاج إلى معالجة.",
  },
  clinic_to_close: {
    en: "Clinic to Close",
    ar: "العيادة المراد إغلاقها",
  },
  closure_details: {
    en: "Closure Details",
    ar: "تفاصيل الإغلاق",
  },
  closure_reason: {
    en: "Reason for Closure",
    ar: "سبب الإغلاق",
  },
  enter_closure_reason: {
    en: "Enter the reason for closing your clinic...",
    ar: "أدخل سبب إغلاق عيادتك...",
  },
  closure_date: {
    en: "Closure Date",
    ar: "تاريخ الإغلاق",
  },
  temporary_closure_end_date: {
    en: "Expected Reopening Date",
    ar: "تاريخ إعادة الافتتاح المتوقع",
  },
  end_date_after_start: {
    en: "End date must be after start date",
    ar: "يجب أن يكون تاريخ الانتهاء بعد تاريخ البدء",
  },
  request_closure: {
    en: "Request Closure",
    ar: "طلب الإغلاق",
  },
  confirm_closure_request: {
    en: "Confirm Closure Request",
    ar: "تأكيد طلب الإغلاق",
  },
  confirm_closure_message: {
    en: "Are you sure you want to submit a closure request for this clinic? You will need to upload required documents after this step.",
    ar: "هل أنت متأكد من رغبتك في تقديم طلب إغلاق لهذه العيادة؟ ستحتاج إلى رفع المستندات المطلوبة بعد هذه الخطوة.",
  },
  confirm_existing_closure_message: {
    en: "Are you sure you want to submit a closure request for this clinic? You will need to upload required documents after this step.",
    ar: "هل أنت متأكد من رغبتك في تقديم طلب إغلاق لهذه العيادة؟ ستحتاج إلى رفع المستندات المطلوبة بعد هذه الخطوة.",
  },
  failed_to_create_closure_request: {
    en: "Failed to create closure request",
    ar: "فشل في إنشاء طلب الإغلاق",
  },
  existing_clinic_info_title: {
    en: "Existing Clinic Closure",
    ar: "إغلاق عيادة قائمة",
  },
  existing_clinic_info_message: {
    en: "Use this form if you have a clinic that was never registered through this system and you want to close it.",
    ar: "استخدم هذا النموذج إذا كانت لديك عيادة لم يتم تسجيلها من خلال هذا النظام وترغب في إغلاقها.",
  },
  clinic_details: {
    en: "Clinic Details",
    ar: "تفاصيل العيادة",
  },
  phone_primary: {
    en: "Primary Phone",
    ar: "الهاتف الرئيسي",
  },
  phone_secondary: {
    en: "Secondary Phone",
    ar: "الهاتف الثانوي",
  },
  enter_phone_number: {
    en: "Enter phone number",
    ar: "أدخل رقم الهاتف",
  },
  invalid_phone_number: {
    en: "Invalid phone number",
    ar: "رقم الهاتف غير صالح",
  },
  // Closure Documents Screen
  upload_closure_documents: {
    en: "Upload Documents",
    ar: "رفع المستندات",
  },
  closure_documents_subtitle: {
    en: "Required documents for closure request",
    ar: "المستندات المطلوبة لطلب الإغلاق",
  },
  upload_documents_info_title: {
    en: "Upload Required Documents",
    ar: "رفع المستندات المطلوبة",
  },
  upload_documents_info_message: {
    en: "Please upload the required documents to complete your closure request. Accepted formats: JPG, PNG, PDF (max 10MB each).",
    ar: "يرجى رفع المستندات المطلوبة لإكمال طلب الإغلاق. الصيغ المقبولة: JPG, PNG, PDF (الحد الأقصى 10 ميجابايت لكل ملف).",
  },
  clinic_signboard_removal_outer: {
    en: "Clinic Signboard Removal (Outer)",
    ar: "إزالة لافتة العيادة (الخارجية)",
  },
  clinic_signboard_removal_outer_description: {
    en: "Photo showing the outer signboard has been removed",
    ar: "صورة توضح إزالة اللافتة الخارجية",
  },
  clinic_signboard_removal_inner: {
    en: "Clinic Signboard Removal (Inner)",
    ar: "إزالة لافتة العيادة (الداخلية)",
  },
  clinic_signboard_removal_inner_description: {
    en: "Photo showing the inner signboard has been removed",
    ar: "صورة توضح إزالة اللافتة الداخلية",
  },
  closure_photos_note: {
    en: "Please upload photos showing the removed clinic signboards and current state of the clinic rooms.",
    ar: "يرجى رفع صور توضح إزالة لافتات العيادة والحالة الحالية لغرف العيادة.",
  },
  please_upload_signboard_removal_outer: {
    en: "Please upload outer signboard removal photo",
    ar: "يرجى رفع صورة إزالة اللافتة الخارجية",
  },
  please_upload_signboard_removal_inner: {
    en: "Please upload inner signboard removal photo",
    ar: "يرجى رفع صورة إزالة اللافتة الداخلية",
  },
  multiple_files: {
    en: "Multiple files allowed",
    ar: "يمكن رفع ملفات متعددة",
  },
  add_more_photos: {
    en: "Add More Photos",
    ar: "إضافة المزيد من الصور",
  },
  select_files: {
    en: "Select Files",
    ar: "اختر الملفات",
  },
  select_file: {
    en: "Select File",
    ar: "اختر ملف",
  },
  some_uploads_failed: {
    en: "Some documents failed to upload. Please try again.",
    ar: "فشل رفع بعض المستندات. يرجى المحاولة مرة أخرى.",
  },
  submission_failed: {
    en: "Failed to submit documents",
    ar: "فشل تقديم المستندات",
  },
  document_uploaded: {
    en: "Document uploaded",
    ar: "تم رفع المستند",
  },
  // Closure Success Screen
  closure_request_submitted: {
    en: "Closure Request Submitted",
    ar: "تم تقديم طلب الإغلاق",
  },
  request_submitted_successfully: {
    en: "Request Submitted Successfully",
    ar: "تم تقديم الطلب بنجاح",
  },
  closure_request_processing_message: {
    en: "Your closure request has been submitted and is being processed. You will be notified of any updates.",
    ar: "تم تقديم طلب الإغلاق الخاص بك وهو قيد المعالجة. سيتم إخطارك بأي تحديثات.",
  },
  what_happens_next: {
    en: "What happens next?",
    ar: "ماذا يحدث بعد ذلك؟",
  },
  closure_step_1: {
    en: "Your request will be reviewed by our team",
    ar: "سيتم مراجعة طلبك من قبل فريقنا",
  },
  closure_step_2: {
    en: "You may be contacted for additional information",
    ar: "قد يتم الاتصال بك للحصول على معلومات إضافية",
  },
  closure_step_3: {
    en: "You will receive a notification when your request is processed",
    ar: "ستتلقى إشعاراً عند معالجة طلبك",
  },
  view_my_requests: {
    en: "View My Requests",
    ar: "عرض طلباتي",
  },
  invalid_date: {
    en: "Invalid date",
    ar: "تاريخ غير صالح",
  },
  // =====================================
  // PROGRESS TRACKING TRANSLATIONS
  // =====================================
  // Request Types
  registration_request: {
    en: "Registration Request",
    ar: "طلب التسجيل",
  },
  closure_request: {
    en: "Closure Request",
    ar: "طلب الإغلاق",
  },
  relocation_request: {
    en: "Relocation Request",
    ar: "طلب النقل",
  },
  renewal_request: {
    en: "Renewal Request",
    ar: "طلب التجديد",
  },
  registration: {
    en: "Registration",
    ar: "التسجيل",
  },
  closure: {
    en: "Closure",
    ar: "الإغلاق",
  },
  relocation: {
    en: "Relocation",
    ar: "النقل",
  },
  renewal: {
    en: "Renewal",
    ar: "التجديد",
  },
  // Status
  draft: {
    en: "Draft",
    ar: "مسودة",
  },
  in_review: {
    en: "In Review",
    ar: "قيد المراجعة",
  },
  approved: {
    en: "Approved",
    ar: "موافق عليه",
  },
  rejected: {
    en: "Rejected",
    ar: "مرفوض",
  },
  in_progress: {
    en: "In Progress",
    ar: "قيد التنفيذ",
  },
  // Stages
  request_info: {
    en: "Request Information",
    ar: "معلومات الطلب",
  },
  payment: {
    en: "Payment",
    ar: "الدفع",
  },
  inspection: {
    en: "Inspection",
    ar: "التفتيش",
  },
  approval: {
    en: "Final Approval",
    ar: "الموافقة النهائية",
  },
  // Document Checklist
  documents_checklist: {
    en: "Documents Checklist",
    ar: "قائمة المستندات",
  },
  add_more: {
    en: "Add More",
    ar: "إضافة المزيد",
  },
  multiple_files_allowed: {
    en: "Multiple files allowed",
    ar: "يمكن رفع ملفات متعددة",
  },
  missing_required_docs: {
    en: "required document(s) pending",
    ar: "مستند(ات) مطلوبة معلقة",
  },
  // Pending Actions
  pending_actions: {
    en: "Pending Actions",
    ar: "الإجراءات المعلقة",
  },
  all_caught_up: {
    en: "All caught up!",
    ar: "لا توجد إجراءات معلقة!",
  },
  no_pending_actions: {
    en: "You have no pending actions",
    ar: "ليس لديك إجراءات معلقة",
  },
  view_all_actions: {
    en: "View all actions",
    ar: "عرض جميع الإجراءات",
  },
  next: {
    en: "Next",
    ar: "التالي",
  },
  // Progress
  active_requests: {
    en: "Active Requests",
    ar: "الطلبات النشطة",
  },
  no_active_requests: {
    en: "No active requests",
    ar: "لا توجد طلبات نشطة",
  },
  // Payment Screen
  processing: {
    en: "Processing...",
    ar: "جاري المعالجة...",
  },
  pay_with_qi_card: {
    en: "Pay with QI Card",
    ar: "ادفع بواسطة QI Card",
  },
  skip_for_testing: {
    en: "Skip for Testing",
    ar: "تخطي للاختبار",
  },
  verifying_payment: {
    en: "Verifying payment...",
    ar: "جاري التحقق من الدفع...",
  },
  check_payment_status: {
    en: "Check Payment Status",
    ar: "التحقق من حالة الدفع",
  },
  auto_checking: {
    en: "(Auto-checking)",
    ar: "(مراقبة تلقائية)",
  },
  cancel_payment: {
    en: "Cancel Payment",
    ar: "إلغاء الدفع",
  },
  fees: {
    en: "Fees",
    ar: "الرسوم",
  },
  registration_fee_iqd: {
    en: "Registration fee is {amount} Iraqi Dinars",
    ar: "أجور التسجيل هي {amount} دينار عراقي",
  },
  closure_fee_iqd: {
    en: "Closure fee is {amount} Iraqi Dinars",
    ar: "أجور الإغلاق هي {amount} دينار عراقي",
  },
  payment_method: {
    en: "Payment Method",
    ar: "طريقة الدفع",
  },
  qi_card_description: {
    en: "Secure electronic payment system in Iraq",
    ar: "نظام الدفع الإلكتروني الآمن في العراق",
  },
  auto_monitoring_active: {
    en: "Auto-monitoring active - checking payment status every 20 seconds",
    ar: "المراقبة التلقائية نشطة - نتحقق من حالة الدفع كل 20 ثانية",
  },
  manual_check_hint: {
    en: "You can click the button above to manually check anytime",
    ar: "يمكنك الضغط على الزر أعلاه للتحقق يدوياً في أي وقت",
  },
  secure_payment: {
    en: "Secure Payment",
    ar: "دفع آمن",
  },
  secure_payment_description: {
    en: "You will be redirected to the secure QI platform to complete your payment. Payment information is protected with industry-standard encryption.",
    ar: "سيتم توجيهك إلى منصة QI الآمنة لإكمال عملية الدفع. معلومات الدفع محمية بتشفير عالي المستوى.",
  },
  payment_instructions: {
    en: "Payment Instructions",
    ar: "تعليمات الدفع",
  },
  redirect_to_qi: {
    en: "You will be redirected to QI page to complete payment",
    ar: "سيتم توجيهك إلى صفحة QI لإكمال الدفع",
  },
  required_amount_iqd: {
    en: "Required amount: {amount} Iraqi Dinars",
    ar: "المبلغ المطلوب: {amount} دينار عراقي",
  },
  auto_verify_payment: {
    en: "Payment will be verified automatically after completion",
    ar: "سيتم التحقق من الدفع تلقائياً بعد الانتهاء",
  },
  payment_url_not_found: {
    en: "Payment URL not found",
    ar: "لم يتم العثور على رابط الدفع",
  },
  failed_initiate_payment: {
    en: "Failed to initiate payment",
    ar: "فشل في بدء عملية الدفع",
  },
  failed_initiate_payment_try_again: {
    en: "Failed to initiate payment. Please try again.",
    ar: "فشل في بدء عملية الدفع. يرجى المحاولة مرة أخرى.",
  },
  payment_was_cancelled_title: {
    en: "Payment cancelled",
    ar: "تم إلغاء الدفع",
  },
  payment_was_cancelled_body: {
    en: "Your request is saved and waiting for payment. You can pay whenever you are ready.",
    ar: "طلبك محفوظ بحالة «بانتظار الدفع». يمكنك الدفع متى شئت.",
  },
  payment_cancelled_successfully: {
    en: "Payment cancelled successfully",
    ar: "تم إلغاء الدفع بنجاح",
  },
  failed_cancel_payment: {
    en: "Failed to cancel payment",
    ar: "فشل في إلغاء الدفع",
  },
  failed_cancel_payment_try_again: {
    en: "Failed to cancel payment. Please try again.",
    ar: "فشل في إلغاء الدفع. يرجى المحاولة مرة أخرى.",
  },
  // AgreeTermsScreen translations
  pledge: {
    en: "Pledge",
    ar: "تعهد",
  },
  i_pledge_following: {
    en: "I pledge the following:",
    ar: "أتعهد بما يلي:",
  },
  terms_already_agreed: {
    en: "Terms have already been agreed to",
    ar: "لقد تمت الموافقة على التعهدات مسبقاً",
  },
  agree_to_all_pledges: {
    en: "Please agree to all required pledges to continue",
    ar: "يرجى الموافقة على جميع التعهدات المطلوبة",
  },
  failed_register_agreement: {
    en: "Failed to register agreement to terms",
    ar: "فشل في تسجيل الموافقة على التعهدات",
  },
  failed_proceed_payment: {
    en: "Failed to proceed to payment",
    ar: "فشل في المتابعة إلى الدفع",
  },
  signature_title: {
    en: "Signature",
    ar: "التوقيع",
  },
  signature_instruction: {
    en: "Please sign inside the box below to confirm your agreement to the pledges.",
    ar: "يرجى التوقيع داخل الإطار أدناه لتأكيد موافقتك على التعهدات.",
  },
  confirm_signature: {
    en: "Confirm & Continue",
    ar: "تأكيد ومتابعة",
  },
  clear_signature: {
    en: "Clear",
    ar: "مسح",
  },
  signature_upload_failed: {
    en: "Failed to upload the signature. Please try again.",
    ar: "فشل رفع التوقيع. يرجى المحاولة مرة أخرى.",
  },
  checking: {
    en: "Checking...",
    ar: "جاري التحقق...",
  },
  must_agree_all_pledges: {
    en: "You must agree to all pledges to continue",
    ar: "يجب الموافقة على جميع التعهدات للمتابعة",
  },
  all_pledges_accepted: {
    en: "All pledges accepted, you can continue now",
    ar: "تم قبول جميع التعهدات، يمكنك المتابعة الآن",
  },
  // ClosureAgreeTermsScreen translations
  closure_pledge: {
    en: "Closure Pledge",
    ar: "تعهد الإغلاق",
  },
  failed_closure_agreement: {
    en: "Failed to register agreement to closure terms",
    ar: "فشل في تسجيل الموافقة على تعهدات الإغلاق",
  },
  surrender_plaque_note_title: {
    en: "Important Reminder",
    ar: "تنبيه مهم",
  },
  surrender_plaque_note_body: {
    en: "When you go to collect your syndicate order, you must surrender your old clinic opening plaque (جدارية فتح العيادة) to the IMA branch.",
    ar: "عند توجهك لاستلام أمر النقابة، يجب عليك تسليم جدارية فتح العيادة القديمة إلى فرع نقابة الأطباء.",
  },
  register: {
    en: "Register",
    ar: "تسجيل",
  },
  // ProfileProgressBar translations
  clinic_info_step: {
    en: "Clinic Info",
    ar: "معلومات العيادة",
  },
  documents_step: {
    en: "Documents",
    ar: "رفع المستندات",
  },
  terms_step: {
    en: "Terms",
    ar: "الشروط والأحكام",
  },
  // Clinic closure translations
  clinic_closure: {
    en: "Clinic Closure",
    ar: "إغلاق العيادة",
  },
  closure_request_details: {
    en: "Closure Request Details",
    ar: "تفاصيل طلب الإغلاق",
  },
  closure_request_pending_message: {
    en: "Your closure request is being processed.",
    ar: "جاري معالجة طلب الإغلاق الخاص بك.",
  },
  closure_request_review_message: {
    en: "Your closure request is under review.",
    ar: "طلب الإغلاق الخاص بك قيد المراجعة.",
  },
  confirm_registered_closure_message: {
    en: "Are you sure you want to close this clinic? This action cannot be undone.",
    ar: "هل أنت متأكد من رغبتك في إغلاق هذه العيادة؟ لا يمكن التراجع عن هذا الإجراء.",
  },
  date_cannot_be_in_past: {
    en: "Date cannot be in the past",
    ar: "لا يمكن أن يكون التاريخ في الماضي",
  },
  back_to_home: {
    en: "Back to Home",
    ar: "العودة إلى الرئيسية",
  },
  // Corrections screen translations
  documents_to_reupload: {
    en: "Documents to Re-upload",
    ar: "المستندات المطلوب إعادة رفعها",
  },
  fields_to_update: {
    en: "Fields to Update",
    ar: "الحقول المطلوب تحديثها",
  },
  failed_to_upload_document: {
    en: "Failed to upload document",
    ar: "فشل في رفع المستند",
  },
  // Form validation translations
  field_min_length: {
    en: "Minimum {min} characters",
    ar: "الحد الأدنى {min} حرف",
  },
  field_max_length: {
    en: "Maximum {max} characters",
    ar: "الحد الأقصى {max} حرف",
  },
  field_must_be_number: {
    en: "Must be a number",
    ar: "يجب أن يكون رقماً",
  },
  failed_to_submit: {
    en: "Failed to submit",
    ar: "فشل في الإرسال",
  },
  // Doctor info placeholders
  graduation_year_placeholder: {
    en: "1990",
    ar: "1990",
  },
  years_of_experience_placeholder: {
    en: "0",
    ar: "0",
  },
  add_tax_number: {
    en: "Enter tax number",
    ar: "أدخل الرقم الضريبي",
  },
  association_specializations_order_number: {
    en: "Association Specializations Order Number",
    ar: "رقم أمر تخصصات النقابة",
  },
  review_application: {
    en: "Review Your Application",
    ar: "مراجعة طلبك",
  },
  review_subtitle: {
    en: "Please review your information before submitting",
    ar: "يرجى مراجعة معلوماتك قبل التقديم",
  },
  onboarding_already_submitted: {
    en: "Your request was submitted when the required documents finished uploading. It is now waiting for the syndicate's review.",
    ar: "تم تقديم طلبك عند اكتمال رفع المستندات المطلوبة، وهو الآن بانتظار مراجعة النقابة.",
  },
  onboarding_info_not_editable: {
    en: "The details of a saved onboarding request cannot be changed in the app yet. If something is wrong, contact support before you upload the documents.",
    ar: "لا يمكن حالياً تعديل بيانات طلب ضم العيادة بعد حفظه في التطبيق. إذا كانت هناك معلومة خاطئة، تواصل مع الدعم قبل رفع المستندات.",
  },
  upload_and_submit_request: {
    en: "Upload & submit request",
    ar: "رفع وتقديم الطلب",
  },
  onboarding_upload_submits_note: {
    en: "The request is submitted as soon as the required documents finish uploading. Check your photos and documents before you press the button.",
    ar: "يُقدَّم الطلب فور اكتمال رفع المستندات المطلوبة. تأكد من الصور والمستندات قبل الضغط على الزر.",
  },
  uploaded_documents: {
    en: "Uploaded Documents",
    ar: "المستندات المرفوعة",
  },
  edit: {
    en: "Edit",
    ar: "تعديل",
  },
  submit_application: {
    en: "Submit Application",
    ar: "تقديم الطلب",
  },
  signboard_outer: {
    en: "Outer Signboard",
    ar: "اللوحة الخارجية",
  },
  signboard_inner: {
    en: "Inner Signboard",
    ar: "اللوحة الداخلية",
  },
  clinic_rooms_photos: {
    en: "Clinic Rooms",
    ar: "غرف العيادة",
  },
  no_clinic_in_residence_doc: {
    en: "No Clinic in Residence Governorate",
    ar: "عدم وجود عيادة في محافظة السكن",
  },
  syndicate_order_doc: {
    en: "Syndicate Order",
    ar: "أمر النقابة",
  },
  opening_certificate_doc: {
    en: "Opening Certificate",
    ar: "شهادة الافتتاح",
  },
  not_uploaded: {
    en: "Not uploaded",
    ar: "لم يتم الرفع",
  },
  photos_count: {
    en: "photos",
    ar: "صور",
  },
  loading_failed: {
    en: "Failed to load data. Please try again.",
    ar: "فشل في تحميل البيانات. يرجى المحاولة مرة أخرى.",
  },
  no_details_provided: {
    en: "No additional details provided",
    ar: "لم يتم تقديم تفاصيل إضافية",
  },
  confirm_update_request: {
    en: "Confirm Update",
    ar: "تأكيد التحديث",
  },
  confirm_update_closure_message: {
    en: "Are you sure you want to update your closure request?",
    ar: "هل أنت متأكد من تحديث طلب الإغلاق؟",
  },
  save_changes: {
    en: "Save Changes",
    ar: "حفظ التغييرات",
  },
};

// Backend error-code translations.
// Keys are the `message_code` values from the API contract (see
// IMA-clinic-registration-api/docs/api/error-codes.md). Placeholders like
// {field} or {retry_after} are filled from `error.details`. Missing
// optional params collapse to "" and surrounding whitespace is trimmed.
const errorTranslations: Translations = {
  // 4.1 Generic
  "common.not_found": {
    en: "Not found",
    ar: "غير موجود",
  },
  "common.access_denied": {
    en: "Access denied",
    ar: "تم رفض الوصول",
  },
  "common.invalid_request": {
    en: "Invalid request",
    ar: "طلب غير صالح",
  },
  "common.internal": {
    en: "Internal server error",
    ar: "حدث خطأ داخلي في الخادم",
  },
  "common.rate_limit": {
    en: "Too many requests. Please try again in {retry_after} seconds.",
    ar: "عدد الطلبات كبير جدًا. يرجى المحاولة مرة أخرى خلال {retry_after} ثانية.",
  },
  "error.unknown": {
    en: "Unknown error",
    ar: "خطأ غير معروف",
  },

  // 4.2 Validation
  "validation.error": {
    en: "Validation error",
    ar: "خطأ في التحقق من البيانات",
  },
  "validation.required": {
    en: "{field} is required",
    ar: "{field} مطلوب",
  },
  "validation.string": {
    en: "{field} must be a string",
    ar: "{field} يجب أن يكون نصًا",
  },
  "validation.integer": {
    en: "{field} must be an integer",
    ar: "{field} يجب أن يكون رقمًا صحيحًا",
  },
  "validation.float": {
    en: "{field} must be a number",
    ar: "{field} يجب أن يكون رقمًا",
  },
  "validation.boolean": {
    en: "{field} must be true or false",
    ar: "{field} يجب أن يكون صحيحًا أو خطأ",
  },
  "validation.min_value": {
    en: "{field} is too small",
    ar: "قيمة {field} أصغر من الحد المسموح",
  },
  "validation.max_value": {
    en: "{field} is too large",
    ar: "قيمة {field} أكبر من الحد المسموح",
  },
  "validation.string_too_short": {
    en: "{field} must be at least {min_length} characters long",
    ar: "{field} يجب أن يحتوي على {min_length} حرفًا على الأقل",
  },
  "validation.string_too_long": {
    en: "{field} must be at most {max_length} characters long",
    ar: "{field} يجب ألا يتجاوز {max_length} حرفًا",
  },
  "validation.invalid": {
    en: "Invalid value",
    ar: "قيمة غير صالحة",
  },

  // 4.3 Auth
  "auth.token.missing": {
    en: "Authentication token is missing",
    ar: "رمز المصادقة مفقود",
  },
  "auth.token.invalid": {
    en: "Invalid token",
    ar: "رمز المصادقة غير صالح",
  },
  "auth.token.expired": {
    en: "Token has expired",
    ar: "انتهت صلاحية رمز المصادقة",
  },
  "auth.credentials.invalid": {
    en: "Could not validate credentials",
    ar: "تعذر التحقق من بيانات الاعتماد",
  },
  "auth.credentials.missing": {
    en: "Missing user information",
    ar: "معلومات المستخدم مفقودة",
  },
  "auth.permission.denied": {
    en: "Insufficient permissions",
    ar: "ليست لديك الصلاحيات الكافية",
  },
  "auth.permission.required": {
    en: "{role} access required",
    ar: "هذا الإجراء يتطلب صلاحية {role}",
  },
  "auth.user.not_found": {
    en: "User not found",
    ar: "المستخدم غير موجود",
  },
  "auth.user.inactive": {
    en: "User account is inactive {reason}",
    ar: "حساب المستخدم غير مفعّل {reason}",
  },

  // 4.4 User
  "user.not_found": {
    en: "User not found",
    ar: "المستخدم غير موجود",
  },
  "user.access_denied": {
    en: "You do not have permission to perform this user action {reason}",
    ar: "ليست لديك صلاحية تنفيذ هذا الإجراء على المستخدم {reason}",
  },
  "user.create.failed": {
    en: "Could not create user {reason}",
    ar: "تعذر إنشاء المستخدم {reason}",
  },
  "user.update.failed": {
    en: "Could not update user {reason}",
    ar: "تعذر تحديث بيانات المستخدم {reason}",
  },
  "user.suspended": {
    en: "User is suspended {reason}",
    ar: "تم تعليق حساب المستخدم {reason}",
  },

  // 4.5 Doctor
  "doctor.not_found": {
    en: "Doctor not found",
    ar: "الطبيب غير موجود",
  },
  "doctor.license.expired": {
    en: "Your medical license has expired. Please renew your license before proceeding.",
    ar: "انتهت صلاحية إجازتك الطبية. يرجى تجديد الإجازة قبل المتابعة.",
  },
  "doctor.license.not_found": {
    en: "No doctor found with license number {license_number}. Please verify your license number.",
    ar: "لا يوجد طبيب مسجَّل برقم الإجازة {license_number}. يرجى التحقق من رقم الإجازة.",
  },
  "doctor.license.required": {
    en: "License number is required",
    ar: "رقم الإجازة مطلوب",
  },
  "doctor.license.already_registered": {
    en: "Medical license number already registered",
    ar: "رقم الإجازة الطبية مسجَّل مسبقًا",
  },
  "doctor.phone.duplicate": {
    en: "Multiple doctors found with this phone number. Please contact support to resolve this issue.",
    ar: "تم العثور على أكثر من طبيب بنفس رقم الهاتف. يرجى التواصل مع الدعم لحل المشكلة.",
  },
  "doctor.phone.required": {
    en: "Phone number is required",
    ar: "رقم الهاتف مطلوب",
  },
  "doctor.registration.not_found": {
    en: "Doctor with registration number {registration_number} not found in database",
    ar: "لا يوجد طبيب مسجَّل برقم التسجيل {registration_number} في قاعدة البيانات",
  },
  "doctor.registration.required": {
    en: "Registration number is required",
    ar: "رقم التسجيل مطلوب",
  },
  "doctor.registration.invalid": {
    en: "Invalid registration number: cannot be all zeros",
    ar: "رقم التسجيل غير صالح: لا يمكن أن يكون أصفارًا فقط",
  },
  "doctor.syndicate.unmapped": {
    en: "Your syndicate branch could not be identified. Please visit your local syndicate branch to update your membership record before proceeding.",
    ar: "تعذّر تحديد فرع نقابتك. يرجى مراجعة فرع النقابة المحلي لتحديث سجل عضويتك قبل المتابعة.",
  },
  "doctor.signup.already_completed": {
    en: "Signup already completed",
    ar: "تم استكمال التسجيل مسبقًا",
  },

  // 4.6 OTP
  "otp.invalid": {
    en: "Invalid or expired OTP code",
    ar: "رمز التأكيد غير صحيح أو منتهي الصلاحية",
  },
  "otp.rate_limited": {
    en: "Too many OTP requests. Please try again in {retry_after} seconds.",
    ar: "عدد طلبات رمز التأكيد كبير جدًا. يرجى المحاولة مرة أخرى خلال {retry_after} ثانية.",
  },
  "qr.identity.exists": {
    en: "An account already exists for this licence number. Sign in with your licence number and the phone number registered on it. If that phone number has changed, contact the syndicate.",
    ar: "يوجد حساب مسجّل برقم الإجازة هذا. سجّل الدخول برقم الإجازة ورقم الهاتف المسجّل عليه. إذا تغيّر رقم هاتفك، راجع النقابة.",
  },
  "otp.send.failed": {
    en: "Failed to send OTP. Please try again later.",
    ar: "تعذر إرسال رمز التأكيد. يرجى المحاولة لاحقًا.",
  },
  "otp.verify.failed": {
    en: "Failed to verify OTP",
    ar: "تعذر التحقق من رمز التأكيد",
  },

  // 4.7 Clinic
  "clinic.not_found": {
    en: "Clinic not found",
    ar: "العيادة غير موجودة",
  },
  "clinic.registration.not_found": {
    en: "Registration request not found",
    ar: "طلب التسجيل غير موجود",
  },
  "clinic.registration.access_denied": {
    en: "You are not authorized to access this registration request",
    ar: "ليست لديك صلاحية الوصول إلى طلب التسجيل هذا",
  },
  "clinic.closure.not_found": {
    en: "Closure request not found",
    ar: "طلب الإغلاق غير موجود",
  },
  "clinic.closure.access_denied": {
    en: "Not authorized to access this closure request",
    ar: "ليست لديك صلاحية الوصول إلى طلب الإغلاق هذا",
  },
  "clinic.closure.invalid_status": {
    en: "This closure request cannot be acted on in its current state ({current_status})",
    ar: "لا يمكن تنفيذ هذا الإجراء على طلب الإغلاق في حالته الحالية ({current_status})",
  },
  "clinic.closure.create.failed": {
    en: "Failed to create closure request",
    ar: "تعذر إنشاء طلب الإغلاق",
  },
  "clinic.closure.update.failed": {
    en: "Failed to update closure request",
    ar: "تعذر تحديث طلب الإغلاق",
  },
  "clinic.onboarding.not_found": {
    en: "Onboarding request not found",
    ar: "طلب الانضمام غير موجود",
  },

  // 4.8 Clinic request lifecycle
  "clinic.request.not_found": {
    en: "Request not found",
    ar: "الطلب غير موجود",
  },
  "clinic.request.access_denied": {
    en: "Not authorized to view this request",
    ar: "ليست لديك صلاحية الاطلاع على هذا الطلب",
  },
  "clinic.request.invalid_status": {
    en: "Cannot perform this action in the current request status ({current_status})",
    ar: "لا يمكن تنفيذ هذا الإجراء في الحالة الحالية للطلب ({current_status})",
  },
  "clinic.request.already_submitted": {
    en: "An active request already exists",
    ar: "يوجد طلب نشط مسبقًا",
  },
  "clinic.request.already_approved": {
    en: "Request has already been approved",
    ar: "تمت الموافقة على الطلب مسبقًا",
  },
  "clinic.request.documents.missing": {
    en: "All required documents must be uploaded before submission",
    ar: "يجب رفع جميع المستندات المطلوبة قبل إرسال الطلب",
  },
  "clinic.request.board.invalid_status": {
    en: "Action requires the request to be in TO_BOARD status (current: {current_status})",
    ar: "يتطلب هذا الإجراء أن يكون الطلب في حالة TO_BOARD (الحالي: {current_status})",
  },
  "clinic.request.create.failed": {
    en: "Failed to create registration request",
    ar: "تعذر إنشاء طلب التسجيل",
  },
  "clinic.request.submit.failed": {
    en: "Failed to submit request",
    ar: "تعذر إرسال الطلب",
  },
  "clinic.request.cancel.failed": {
    en: "Failed to cancel request",
    ar: "تعذر إلغاء الطلب",
  },
  "clinic.request.terms.failed": {
    en: "Failed to accept terms",
    ar: "تعذر قبول الشروط",
  },

  // 4.9 Corrections
  "corrections.not_found": {
    en: "Request not found",
    ar: "الطلب غير موجود",
  },
  "corrections.invalid_status": {
    en: "Corrections cannot be applied in the current status ({current_status})",
    ar: "لا يمكن تطبيق التصحيحات في الحالة الحالية ({current_status})",
  },

  // 4.10 Payment
  "payment.not_found": {
    en: "Payment not found",
    ar: "عملية الدفع غير موجودة",
  },
  "payment.access_denied": {
    en: "You don't have permission to access this payment",
    ar: "ليست لديك صلاحية الوصول إلى عملية الدفع هذه",
  },
  "payment.terms.required": {
    en: "Cannot initiate payment. Please agree to terms and conditions first. Current status: {current_status}",
    ar: "تعذر بدء عملية الدفع. يرجى الموافقة على الشروط والأحكام أولًا. الحالة الحالية: {current_status}",
  },
  "payment.initiate.failed": {
    en: "Failed to initiate payment",
    ar: "تعذر بدء عملية الدفع",
  },
  "payment.verify.failed": {
    en: "Failed to verify payment",
    ar: "تعذر التحقق من عملية الدفع",
  },
  "payment.cancel.failed": {
    en: "Failed to cancel payment",
    ar: "تعذر إلغاء عملية الدفع",
  },
  "payment.refund.failed": {
    en: "Failed to refund payment",
    ar: "تعذر استرداد المبلغ",
  },

  // 4.11 Inspector payment (admin)
  "inspector.payment.not_found": {
    en: "Inspector payment not found",
    ar: "دفعة المفتش غير موجودة",
  },
  "inspector.payment.invalid_status": {
    en: "Payment is not in a valid state for this action",
    ar: "حالة الدفع لا تسمح بتنفيذ هذا الإجراء",
  },
  "inspector.payment.process.failed": {
    en: "Failed to process inspector payment",
    ar: "تعذر معالجة دفعة المفتش",
  },

  // 4.12 Inspection
  "inspection.not_found": {
    en: "Inspection not found",
    ar: "الكشف غير موجود",
  },
  "inspection.invalid_status": {
    en: "Inspection cannot be acted on in its current status ({current_status})",
    ar: "لا يمكن تنفيذ الإجراء على الكشف في حالته الحالية ({current_status})",
  },
  "inspection.already_completed": {
    en: "Inspection is already submitted",
    ar: "تم تقديم الكشف مسبقًا",
  },
  "inspection.already_approved": {
    en: "Another inspection ({inspection_number}) is already approved for this request",
    ar: "تمت الموافقة مسبقًا على كشف آخر ({inspection_number}) لهذا الطلب",
  },
  "inspection.documents.missing": {
    en: "Document {document_type} must be uploaded before submitting the inspection",
    ar: "يجب رفع المستند {document_type} قبل تقديم الكشف",
  },
  "inspection.inspector.unavailable": {
    en: "Inspector has reached the maximum limit of {max_inspections} inspections this month",
    ar: "وصل المفتش إلى الحد الأقصى ({max_inspections}) من الكشوف لهذا الشهر",
  },

  // 4.13 Violation
  "violation.not_found": {
    en: "Violation not found",
    ar: "المخالفة غير موجودة",
  },
  "violation.invalid_status": {
    en: "Violation cannot be acted on in its current status ({current_status})",
    ar: "لا يمكن تنفيذ هذا الإجراء على المخالفة في حالتها الحالية ({current_status})",
  },
  "violation.already_resolved": {
    en: "Violation is already resolved",
    ar: "تمت معالجة المخالفة مسبقًا",
  },
  "violation.already_closed": {
    en: "Cannot resolve a closed violation",
    ar: "لا يمكن معالجة مخالفة مغلقة",
  },
  "violation.attachment.upload.failed": {
    en: "Failed to upload attachment",
    ar: "تعذر رفع المرفق",
  },

  // 4.14 Attachment
  "attachment.not_found": {
    en: "Attachment not found",
    ar: "المرفق غير موجود",
  },
  "attachment.access_denied": {
    en: "Access denied to this attachment",
    ar: "تم رفض الوصول إلى هذا المرفق",
  },
  "attachment.resource.not_found": {
    en: "Resource not found or access denied",
    ar: "المورد غير موجود أو تم رفض الوصول إليه",
  },
  "attachment.invalid_type": {
    en: "Invalid document type: {document_type}. Valid types: {valid_types}",
    ar: "نوع المستند غير صالح: {document_type}. الأنواع المسموح بها: {valid_types}",
  },
  "attachment.invalid_size": {
    en: "Maximum of {limit} '{document_type}' attachments allowed (already have {existing_count}). Delete one before uploading another.",
    ar: "الحد الأقصى المسموح به هو {limit} مرفقًا من نوع '{document_type}' (يوجد لديك حاليًا {existing_count}). يرجى حذف أحد المرفقات قبل رفع مرفق جديد.",
  },
  // Upload policy (server checks the file's bytes): photos JPG/PNG/WEBP/HEIC or PDF, size limit,
  // and no uploads once a request can no longer be edited.
  "attachment.file_too_large": {
    en: "The file is larger than allowed ({max_size_mb} MB). Choose a smaller file or take a new photo.",
    ar: "حجم الملف أكبر من المسموح ({max_size_mb} ميجابايت). اختر ملفاً أصغر أو التقط صورة جديدة.",
  },
  "attachment.empty_file": {
    en: "The file is empty. Choose another file.",
    ar: "الملف فارغ. اختر ملفاً آخر.",
  },
  "attachment.unsupported_file_type": {
    en: "This file type is not accepted. Choose a photo (JPG, PNG, WEBP, HEIC) or a PDF.",
    ar: "نوع الملف غير مقبول. اختر صورة (JPG أو PNG أو WEBP أو HEIC) أو ملف PDF.",
  },
  "clinic.request.not_editable": {
    en: "This request can no longer be changed. If something needs correcting, the association will ask you for it.",
    ar: "لم يعد بالإمكان تعديل هذا الطلب. إذا احتاج شيء إلى تصحيح فستطلبه منك النقابة.",
  },
  "attachment.upload.failed": {
    en: "Failed to upload attachment",
    ar: "تعذر رفع المرفق",
  },
  "attachment.delete.failed": {
    en: "Failed to delete attachment",
    ar: "تعذر حذف المرفق",
  },
  "attachment.get.failed": {
    en: "Failed to get attachments",
    ar: "تعذر جلب المرفقات",
  },

  // 4.15 File / S3
  "file.not_found": {
    en: "Missing required attachments: {missing_types}",
    ar: "المرفقات المطلوبة مفقودة: {missing_types}",
  },
  "file.upload.failed": {
    en: "Failed to upload file",
    ar: "تعذر رفع الملف",
  },

  // 4.16 Location / Area
  "location.not_found": {
    en: "Location not found",
    ar: "الموقع غير موجود",
  },
  "location.invalid": {
    en: "Invalid location",
    ar: "الموقع غير صالح",
  },
  "location.out_of_range": {
    en: "Inspector must be within {limit_m} meters of the clinic. Current distance: {distance_m}m.",
    ar: "يجب أن يكون المفتش على بعد لا يتجاوز {limit_m} مترًا من العيادة. المسافة الحالية: {distance_m} مترًا.",
  },
  "area.not_found": {
    en: "The following area IDs are not within your workspace's area: {invalid_ids}",
    ar: "المعرّفات التالية للمناطق ليست ضمن مناطق مساحة عملك: {invalid_ids}",
  },

  // 4.17 Reporting
  "report.params.invalid": {
    en: "Invalid report parameters",
    ar: "معاملات التقرير غير صالحة",
  },
  "report.generate.failed": {
    en: "Failed to generate report for resource '{resource}'",
    ar: "تعذر إنشاء التقرير للمورد '{resource}'",
  },

  // 4.18 Webhook
  "webhook.invalid_signature": {
    en: "Invalid webhook signature",
    ar: "توقيع الويب هوك غير صالح",
  },
  "webhook.payload.invalid": {
    en: "Malformed webhook payload",
    ar: "حمولة الويب هوك غير صالحة",
  },
  "webhook.processing.failed": {
    en: "Webhook could not be processed",
    ar: "تعذرت معالجة الويب هوك",
  },

  // 4.19 Misc
  "activity_log.not_found": {
    en: "Activity log not found",
    ar: "سجل النشاط غير موجود",
  },
  "health.check.failed": {
    en: "Service health check failed",
    ar: "فشل فحص حالة الخدمة",
  },
  "db.duplicate": {
    en: "This value already exists",
    ar: "هذه القيمة موجودة مسبقًا",
  },
  "db.foreign_key": {
    en: "Cannot complete operation: related record does not exist or is in use",
    ar: "تعذر إكمال العملية: السجل المرتبط غير موجود أو قيد الاستخدام",
  },
  "db.required": {
    en: "This field is required",
    ar: "هذا الحقل مطلوب",
  },
  "db.check_constraint": {
    en: "Invalid data: constraint violation",
    ar: "بيانات غير صالحة: تم انتهاك أحد القيود",
  },
  "db.integrity": {
    en: "Database integrity error",
    ar: "خطأ في سلامة قاعدة البيانات",
  },
  "db.error": {
    en: "Database error",
    ar: "خطأ في قاعدة البيانات",
  },
};

const interpolate = (template: string, params: Record<string, any>): string => {
  // Replace every {key} with params[key]. Missing keys collapse to "".
  // Collapse the resulting double spaces / trailing whitespace.
  const filled = template.replace(/\{(\w+)\}/g, (_, key) => {
    const value = params?.[key];
    return value === undefined || value === null ? "" : String(value);
  });
  return filled.replace(/\s+/g, " ").trim();
};

/**
 * Localized text for a backend message_code outside React (API services), in the app language
 * stored on this device. Returns null when the code has no translation.
 */
export function translateErrorCode(
  messageCode: string | null | undefined,
  details?: Record<string, any> | null
): string | null {
  if (!messageCode) return null;
  let lang: "ar" | "en" = "ar";
  try {
    lang = localStorage.getItem("app_language") === "en" ? "en" : "ar";
  } catch {
    /* default Arabic */
  }
  const template = errorTranslations[messageCode]?.[lang];
  return template ? interpolate(template, details ?? {}) : null;
}

export interface BackendApiError {
  code?: number;
  message_code?: string;
  message?: string;
  details?: any;
}

interface LanguageContextType {
  language: "en" | "ar";
  setLanguage: (lang: "en" | "ar") => void;
  t: (key: string) => string;
  tError: (error: BackendApiError | null | undefined) => string;
  tValidationErrors: (
    error: BackendApiError | null | undefined
  ) => Array<{ field?: string; message: string }>;
  isRTL: boolean;
}

const LanguageContext = createContext<LanguageContextType | undefined>(
  undefined
);

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  // Load saved language from localStorage or default to 'ar'
  const [language, setLanguageState] = useState<"en" | "ar">(() => {
    const savedLanguage = localStorage.getItem("app_language");
    return savedLanguage === "en" || savedLanguage === "ar"
      ? savedLanguage
      : "ar";
  });

  // Save language to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem("app_language", language);
  }, [language]);

  // An explicit choice is stored for the signed-in doctor (or as the device default when
  // signed out) — src/shared/storage/uiPreferences.ts.
  const setLanguage = useCallback((lang: "en" | "ar") => {
    setLanguageState(lang);
    try {
      recordUiChoice("language", lang, localStorage, sessionStorage);
    } catch {
      /* storage unavailable — the choice still applies for this page */
    }
  }, []);

  // At sign-in the doctor's own preference is applied (never the previous doctor's).
  useEffect(() => {
    const onApplied = (e: Event) => {
      const prefs = (e as CustomEvent<UiPrefs>).detail;
      if (prefs && (prefs.language === "ar" || prefs.language === "en")) {
        setLanguageState(prefs.language);
      }
    };
    window.addEventListener(UI_PREFS_APPLIED_EVENT, onApplied);
    return () => window.removeEventListener(UI_PREFS_APPLIED_EVENT, onApplied);
  }, []);

  // Keep <html lang/dir> in sync. index.html ships with a hardcoded dir="rtl",
  // so without this English users get an RTL document, and anything rendered
  // outside the layout's dir wrapper (portals, which mount on document.body)
  // inherits the wrong direction regardless of the selected language.
  useEffect(() => {
    document.documentElement.lang = language;
    document.documentElement.dir = language === "ar" ? "rtl" : "ltr";
  }, [language]);

  const t = (key: string): string => {
    return translations[key]?.[language] || key;
  };

  const tError = (error: BackendApiError | null | undefined): string => {
    if (!error) return "";
    const { message_code, details, message } = error;
    if (message_code) {
      const template = errorTranslations[message_code]?.[language];
      if (template) {
        return interpolate(template, (details as Record<string, any>) ?? {});
      }
    }
    // Fall back to the backend's English `message` so the UI stays usable
    // when a new code ships before the FE dictionary catches up.
    return message ?? message_code ?? "";
  };

  const tValidationErrors = (
    error: BackendApiError | null | undefined
  ): Array<{ field?: string; message: string }> => {
    const items = error?.details?.errors;
    if (!Array.isArray(items)) return [];
    return items.map((item: any) => {
      const localized = tError({
        message_code: item?.message_code,
        message: item?.message,
        details: item,
      });
      return { field: item?.field, message: localized };
    });
  };

  const isRTL = language === "ar";

  return (
    <LanguageContext.Provider
      value={{ language, setLanguage, t, tError, tValidationErrors, isRTL }}
    >
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};
